import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DndContext, DragOverlay, useDraggable, useDroppable, pointerWithin } from '@dnd-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  ChevronLeft, ChevronRight, Truck, Users, X, Clock, Building2, 
  Search, Maximize2, Minimize2, RefreshCw, GripVertical, UserPlus, Lock, GripHorizontal, Filter, ChevronDown, HardHat, Trash2, AlertTriangle, Loader2, Plus, Wrench, Eye, EyeOff, Moon, Layers
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, subDays, parseISO, eachDayOfInterval } from 'date-fns';
import { CalendarClock } from 'lucide-react';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import QuickReserveDialog from '../components/QuickReserveDialog';
import { formatTime } from '../utils/dateFormatting';
import { getApiErrorMessage } from '../utils/apiErrors';
import { notifyAllocationConflicts } from '../utils/allocationConflicts';
import JobEditSheet from './JobEditSheet';
import JobCreateSheet from './JobCreateSheet';
import LeaveOverrideDialog from '../components/LeaveOverrideDialog';
import { getLeaveBlock, withLeaveOverride } from '../lib/leaveOverride';

// ============================================
// CONSTANTS
// ============================================
const TIMELINE_START_HOUR = 0;  // 12 AM (midnight)
const TIMELINE_END_HOUR = 24;   // 12 AM (next day) - Full 24 hours
const SLOT_WIDTH = 40;          // 40px per 30-min slot
const ROW_HEIGHT = 100;         // Height of each job row (increased for worker slots)
const HEADER_HEIGHT = 40;       // Height of the time header
const MIN_DURATION_HOURS = 0.5; // Minimum 30 minutes

// Generate time slots (30-minute intervals)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = TIMELINE_START_HOUR; hour < TIMELINE_END_HOUR; hour++) {
    slots.push({ hour, minute: 0, label: `${hour.toString().padStart(2, '0')}:00` });
    slots.push({ hour, minute: 30, label: `${hour.toString().padStart(2, '0')}:30` });
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Stable colour-hash for Setup Group IDs so all jobs in a group share the same
// visual tint on the Gantt timeline. Uses HSL so we can control lightness/alpha.
const setupGroupColor = (groupId) => {
  if (!groupId) return null;
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash * 31 + groupId.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return {
    stripe: `hsl(${hue}, 72%, 60%)`,    // strong left-stripe colour
    tint: `hsla(${hue}, 72%, 50%, 0.12)`, // faint fill
    ring: `hsla(${hue}, 72%, 60%, 0.55)`, // outer ring
  };
};

// Convert time string (HH:MM) to pixel offset
const timeToPixel = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const totalMinutes = (hours - TIMELINE_START_HOUR) * 60 + minutes;
  return Math.max(0, (totalMinutes / 30) * SLOT_WIDTH);
};

// Convert pixel offset to time string (HH:MM)
const pixelToTime = (pixel) => {
  const totalMinutes = Math.round((pixel / SLOT_WIDTH) * 30);
  const hours = Math.floor(totalMinutes / 60) + TIMELINE_START_HOUR;
  const minutes = totalMinutes % 60;
  // Snap to 15-minute intervals
  const snappedMinutes = Math.round(minutes / 15) * 15;
  const finalHours = snappedMinutes === 60 ? hours + 1 : hours;
  const finalMinutes = snappedMinutes === 60 ? 0 : snappedMinutes;
  return `${String(Math.min(23, Math.max(0, finalHours))).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
};

// Convert pixel width to duration (hours)
const pixelToDuration = (pixel) => {
  const hours = (pixel / SLOT_WIDTH) * 0.5;
  // Snap to 0.5 hour intervals
  return Math.max(MIN_DURATION_HOURS, Math.round(hours * 2) / 2);
};

// Convert duration (hours) to pixel width
const durationToPixel = (duration) => {
  if (!duration) return SLOT_WIDTH * 2; // Default 1 hour
  return (duration * 60 / 30) * SLOT_WIDTH;
};

// ============================================
// DRAGGABLE COMPONENTS
// ============================================

// Draggable Resource (Asset or Operator)
const DraggableResource = ({ id, type, data, children, isAllocated }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${type}-${id}`,
    data: { type, ...data }
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...listeners} 
      {...attributes}
      className={`${isAllocated ? 'opacity-50' : ''}`}
    >
      {children}
    </div>
  );
};

// Droppable Gantt Bar (Job) - Now just handles drop target highlighting
const DroppableGanttBar = ({ jobId, children }) => {
  const { setNodeRef, isOver } = useDroppable({ 
    id: `job-${jobId}`,
  });

  return (
    <div 
      ref={setNodeRef} 
      className={`w-full h-full relative transition-all duration-150 ${isOver ? 'ring-2 ring-[#ffd108] ring-inset rounded-lg' : ''}`}
    >
      {children}
    </div>
  );
};

// Droppable Worker Slot - Enhanced visibility
const DroppableWorkerSlot = ({ jobId, workerTypeId, index, workerTypeName, children, isAssigned, allocationId }) => {
  const { setNodeRef, isOver } = useDroppable({ 
    id: `worker__${jobId}__${workerTypeId}__${index}`,
    data: { jobId, workerTypeId, index, workerTypeName, allocationId }
  });

  return (
    <div 
      ref={setNodeRef} 
      className={`transition-all duration-150 rounded ${
        isOver 
          ? 'ring-2 ring-[#ffd108] bg-[#ffd108]/30 scale-110 z-20 shadow-lg' 
          : !isAssigned 
            ? 'border border-dashed border-purple-500/50 hover:border-purple-400 hover:bg-purple-500/10' 
            : ''
      }`}
    >
      {children}
    </div>
  );
};

// Droppable Load Strip (Trailer Cargo P2) — accepts asset & attachment drops
const DroppableLoadStrip = ({ allocationId, children, hasItems }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `load-${allocationId}`,
    data: { kind: 'load-strip', allocationId },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={`pb-load-strip-${allocationId}`}
      className={`flex flex-wrap items-center gap-1 px-1.5 py-1 rounded border border-dashed transition-all ${
        isOver
          ? 'border-[#ffd108] bg-[#ffd108]/15 ring-1 ring-[#ffd108]'
          : hasItems
            ? 'border-amber-500/40 bg-amber-500/5'
            : 'border-zinc-600 bg-transparent'
      }`}
      title="Drop assets or attachments here to load them onto this trailer"
    >
      {children}
    </div>
  );
};

// Allocation Bubble Component - Better contrast colors
const AllocationBubble = ({ type, label, color, onRemove }) => {
  // Default colors for equipment and operator, custom color for workers
  const bgColor = type === 'worker' && color 
    ? color 
    : type === 'equipment' 
      ? '#2563EB' 
      : '#059669';

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div 
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white shadow-sm border border-white/20"
            style={{ backgroundColor: bgColor }}
          >
            {type === 'equipment' && <Truck className="h-3 w-3" />}
            {type === 'operator' && <Users className="h-3 w-3" />}
            {type === 'worker' && <UserPlus className="h-3 w-3" />}
            <span className="max-w-[70px] truncate">{label}</span>
            {onRemove && (
              <button 
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="ml-0.5 hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {type === 'equipment' ? 'Asset' : type === 'operator' ? 'Operator' : 'Additional Worker'}: {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ============================================
// RESIZABLE GANTT BAR COMPONENT
// ============================================
const ResizableGanttBar = ({ 
  job, 
  leftOffset, 
  barWidth, 
  minHeight,
  statusColor, 
  isLocked,
  onMove, 
  onResize,
  onBarClick,
  setupGroupTheme,  // { stripe, tint, ring } | null — Setup Group visual grouping
  children 
}) => {
  const barRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [didDrag, setDidDrag] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [initialLeft, setInitialLeft] = useState(leftOffset);
  const [initialWidth, setInitialWidth] = useState(barWidth);
  const [currentLeft, setCurrentLeft] = useState(leftOffset);
  const [currentWidth, setCurrentWidth] = useState(barWidth);

  // Reset position when props change
  useEffect(() => {
    setCurrentLeft(leftOffset);
    setCurrentWidth(barWidth);
  }, [leftOffset, barWidth]);

  // Handle move drag start
  const handleMoveStart = (e) => {
    if (isLocked) return;
    e.stopPropagation();
    setIsDragging(true);
    setDidDrag(false);
    setDragStartX(e.clientX);
    setInitialLeft(currentLeft);
  };

  // Handle resize drag start
  const handleResizeStart = (e) => {
    if (isLocked) return;
    e.stopPropagation();
    setIsResizing(true);
    setDidDrag(false);
    setDragStartX(e.clientX);
    setInitialWidth(currentWidth);
  };

  // Handle mouse move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const delta = e.clientX - dragStartX;
        if (Math.abs(delta) > 3) setDidDrag(true);
        const newLeft = Math.max(0, initialLeft + delta);
        setCurrentLeft(newLeft);
      } else if (isResizing) {
        const delta = e.clientX - dragStartX;
        if (Math.abs(delta) > 3) setDidDrag(true);
        const newWidth = Math.max(SLOT_WIDTH, initialWidth + delta);
        setCurrentWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        // Calculate new start time and call onMove
        const newStartTime = pixelToTime(currentLeft);
        if (onMove && newStartTime !== pixelToTime(leftOffset)) {
          onMove(job.id, newStartTime);
        }
      } else if (isResizing) {
        setIsResizing(false);
        // Calculate new duration and call onResize
        const newDuration = pixelToDuration(currentWidth);
        if (onResize && newDuration !== pixelToDuration(barWidth)) {
          onResize(job.id, newDuration);
        }
      }
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStartX, initialLeft, initialWidth, currentLeft, currentWidth, job.id, onMove, onResize, leftOffset, barWidth]);

  return (
    <div 
      ref={barRef}
      className={`absolute transition-all ${isDragging || isResizing ? 'z-30 shadow-xl' : ''} ${isLocked ? 'cursor-not-allowed' : ''}`}
      style={{ 
        top: 8, 
        left: currentLeft, 
        width: Math.max(currentWidth, 180),
        minHeight: minHeight,
      }}
    >
      <div 
        className={`w-full h-full rounded-lg border-l-4 ${statusColor.bg} ${statusColor.border} transition-all ${!isLocked ? 'hover:brightness-110' : 'opacity-80'} ${isDragging ? 'cursor-grabbing' : ''}`}
        style={setupGroupTheme ? {
          boxShadow: `inset 6px 0 0 ${setupGroupTheme.stripe}, 0 0 0 1.5px ${setupGroupTheme.ring}`,
          backgroundImage: `linear-gradient(90deg, ${setupGroupTheme.tint}, transparent 45%)`,
        } : undefined}
        data-setup-group={job.setup_group_id || undefined}
      >
        {/* Move handle - top bar */}
        {!isLocked && (
          <div 
            className="absolute top-0 left-0 right-4 h-5 cursor-grab flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-gradient-to-b from-black/20 to-transparent rounded-t-lg"
            onMouseDown={handleMoveStart}
          >
            <GripHorizontal className="h-3 w-3 text-white/70" />
          </div>
        )}
        
        {/* Locked indicator */}
        {isLocked && (
          <div className="absolute top-1 right-1">
            <Lock className="h-3 w-3 text-zinc-400" />
          </div>
        )}
        
        {/* Content — click to edit job */}
        <div 
          className="p-2 h-full flex flex-col cursor-pointer"
          onClick={(e) => {
            if (!didDrag && onBarClick) {
              e.stopPropagation();
              onBarClick(job);
            }
          }}
        >
          {children}
        </div>
        
        {/* Resize handle - right edge */}
        {!isLocked && (
          <div 
            className="absolute top-0 right-0 w-2 h-full cursor-ew-resize hover:bg-[#ffd108]/30 transition-colors rounded-r-lg"
            onMouseDown={handleResizeStart}
          />
        )}
      </div>
      
      {/* Time preview while dragging */}
      {(isDragging || isResizing) && (
        <div className="absolute -top-6 left-0 bg-zinc-800 text-white text-[10px] px-2 py-0.5 rounded shadow-lg whitespace-nowrap">
          {pixelToTime(currentLeft)} - {pixelToDuration(currentWidth)}h
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const PlanningBoard = () => {
  const { api, user } = useAuth();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const [pbReserveOpen, setPbReserveOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timelineRef = useRef(null);
  
  // Core data
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [operators, setOperators] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [workerAllocations, setWorkerAllocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  // Trailer Load (P2) — attachment instances for dropping onto trailer allocations
  const [attachments, setAttachments] = useState([]);
  const [allocationWarnings, setAllocationWarnings] = useState({});
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [tenantSettings, setTenantSettings] = useState({ date_format: 'DD/MM/YYYY', time_format: '24h', depot_time_enabled: false });
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    return dateParam ? parseISO(dateParam) : new Date();
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  // Trailer Load (P2) — sidebar tab toggle: 'assets' | 'attachments'
  const [sidebarTab, setSidebarTab] = useState('assets');
  const [attachmentSearch, setAttachmentSearch] = useState('');
  const [operatorSearch, setOperatorSearch] = useState('');
  const [showAllocatedAssets, setShowAllocatedAssets] = useState(true);
  const [showAllocatedOperators, setShowAllocatedOperators] = useState(true);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState([]); // Multi-select filter for asset types
  const [selectedWorkerTypes, setSelectedWorkerTypes] = useState([]); // Multi-select filter for worker types
  const [activeId, setActiveId] = useState(null);
  const [activeData, setActiveData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Multi-day prompt state
  const [multiDayPrompt, setMultiDayPrompt] = useState({ open: false, jobId: null, job: null, action: null, value: null });

  // Job edit sheet state
  const [editSheetJobId, setEditSheetJobId] = useState(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [createSheetPrefill, setCreateSheetPrefill] = useState(null);
  const [deleteJobDialog, setDeleteJobDialog] = useState({ open: false, job: null });
  // Leave override dialog
  const [leaveOverride, setLeaveOverride] = useState({ open: false, blocks: [], callback: null, onCancel: null });
  // Leave badge data from init endpoint
  const [pendingLeaveWorkerIds, setPendingLeaveWorkerIds] = useState(new Set());
  const [partDayLeaveMap, setPartDayLeaveMap] = useState({});
  const [isDeletingJob, setIsDeletingJob] = useState(false);
  const [collapsedAssets, setCollapsedAssets] = useState({});
  const [timelineAssetTypeFilter, setTimelineAssetTypeFilter] = useState([]);
  const [showUnallocatedAssets, setShowUnallocatedAssets] = useState(true);

  const handleConfirmDeleteJob = async () => {
    if (!deleteJobDialog.job) return;
    const job = deleteJobDialog.job;
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    const dateStr = format(currentDate, 'yyyy-MM-dd');

    setIsDeletingJob(true);
    try {
      if (isMultiDay) {
        // Only remove the current day from the multi-day job
        const addDaysUtil = (ds, days) => {
          const [y, m, d] = ds.split('-').map(Number);
          return new Date(Date.UTC(y, m - 1, d + days)).toISOString().split('T')[0];
        };

        const allDates = [];
        let cur = new Date(job.start_date + 'T00:00:00Z');
        const end = new Date(job.end_date + 'T00:00:00Z');
        while (cur <= end) {
          allDates.push(cur.toISOString().split('T')[0]);
          cur.setUTCDate(cur.getUTCDate() + 1);
        }

        if (allDates.length <= 1) {
          // Last day — delete the whole job
          await api.delete(`/jobs/${job.id}`);
          toast.success('Job deleted');
        } else if (dateStr === job.start_date) {
          const newStart = addDaysUtil(dateStr, 1);
          const _r = await api.put(`/jobs/${job.id}`, { ...buildPBJobUpdatePayload(job), start_date: newStart });
          toast.success(`Removed ${formatDateDisplay(dateStr)} — job now starts ${formatDateDisplay(newStart)}`);
          notifyAllocationConflicts(_r.data);
        } else if (dateStr === job.end_date) {
          const newEnd = addDaysUtil(dateStr, -1);
          const _r = await api.put(`/jobs/${job.id}`, { ...buildPBJobUpdatePayload(job), end_date: newEnd });
          toast.success(`Removed ${formatDateDisplay(dateStr)} — job now ends ${formatDateDisplay(newEnd)}`);
          notifyAllocationConflicts(_r.data);
        } else {
          // Middle day — SPLIT: keep days before, create new job for days after
          const newEndForOriginal = addDaysUtil(dateStr, -1);
          const newStartForSplit = addDaysUtil(dateStr, 1);
          const originalEnd = job.end_date;

          // 1. Shrink original job
          await api.put(`/jobs/${job.id}`, { ...buildPBJobUpdatePayload(job), end_date: newEndForOriginal });

          // 2. Create new job for remaining days
          const splitPayload = {
            customer_id: job.customer_id,
            contact_id: job.contact_id || undefined,
            site_id: job.site_id || '',
            site_contact_id: job.site_contact_id || undefined,
            one_off_street: job.one_off_street || '',
            one_off_suburb: job.one_off_suburb || '',
            one_off_state: job.one_off_state || '',
            one_off_postcode: job.one_off_postcode || '',
            equipment_type: job.equipment_type,
            attachment_type_ids: job.attachment_type_ids || [],
            worker_requirements: job.worker_requirements || [],
            start_date: newStartForSplit,
            start_time: job.start_time || '',
            end_time: job.end_time || undefined,
            depot_time: job.depot_time || undefined,
            end_date: originalEnd,
            est_duration: job.est_duration || undefined,
            description: job.description || '',
            status: 'not_allocated',
            notes: job.notes || '',
            tags: job.tags || [],
            po_number: job.po_number || '',
          };
          const newJobRes = await api.post('/jobs', splitPayload);
          const newJobId = newJobRes.data?.id;

          // 3. Migrate allocations covering the split range to the new job
          if (newJobId) {
            const splitAllocations = allocations.filter(a =>
              a.job_id === job.id &&
              (a.end_date || a.start_date) >= newStartForSplit
            );
            const newJobBlocks = [];
            for (const alloc of splitAllocations) {
              const allocStart = alloc.start_date < newStartForSplit ? newStartForSplit : alloc.start_date;
              const allocEnd = alloc.end_date || alloc.start_date;
              try {
                const res = await api.post('/allocations?skip_validation=true', {
                  job_id: newJobId,
                  equipment_id: alloc.equipment_id || '',
                  operator_id: alloc.operator_id || '',
                  start_date: allocStart,
                  end_date: allocEnd > originalEnd ? originalEnd : allocEnd,
                  attachment_ids: alloc.attachment_ids || [],
                });
                if (res.data?.id) newJobBlocks.push(res.data);
              } catch (e) { /* best effort */ }
            }

            // 4. Migrate worker allocations
            const splitWorkerAllocs = workerAllocations.filter(wa => {
              const waEnd = wa.end_date || job.end_date || wa.start_date;
              return wa.job_id === job.id && waEnd >= newStartForSplit;
            });
            for (const wa of splitWorkerAllocs) {
              const waStart = wa.start_date < newStartForSplit ? newStartForSplit : wa.start_date;
              const waEnd = wa.end_date || job.end_date || wa.start_date;
              const crewEnd = waEnd > originalEnd ? originalEnd : waEnd;
              // Link to the new job's block when one block covers the whole range; otherwise the backend links/splits
              const coveringBlocks = newJobBlocks.filter(b => b.start_date <= waStart && (b.end_date || b.start_date) >= crewEnd);
              try {
                await api.post('/worker-allocations', {
                  job_id: newJobId,
                  worker_type_id: wa.worker_type_id,
                  operator_id: wa.operator_id,
                  start_date: waStart,
                  end_date: crewEnd,
                  ...(coveringBlocks.length === 1 ? { allocation_id: coveringBlocks[0].id } : {}),
                });
              } catch (e) { /* best effort */ }
            }
          }

          // 5. Trim stale allocations on the original job
          const staleAllocations = allocations.filter(a =>
            a.job_id === job.id && (a.end_date || a.start_date) > newEndForOriginal
          );
          for (const alloc of staleAllocations) {
            try {
              if (alloc.start_date > newEndForOriginal) {
                await api.delete(`/allocations/${alloc.id}`);
              } else {
                await api.patch(`/allocations/${alloc.id}?skip_validation=true`, { end_date: newEndForOriginal });
              }
            } catch (e) { /* best effort */ }
          }
          const staleWorkerAllocs = workerAllocations.filter(wa => {
            const waEnd = wa.end_date || job.end_date || wa.start_date;
            return wa.job_id === job.id && waEnd > newEndForOriginal;
          });
          for (const wa of staleWorkerAllocs) {
            try {
              if (wa.start_date > newEndForOriginal) {
                await api.delete(`/worker-allocations/${wa.id}`);
              } else {
                await api.patch(`/worker-allocations/${wa.id}`, { end_date: newEndForOriginal });
              }
            } catch (e) { /* best effort */ }
          }

          toast.success(`Day removed — job split into two: ends ${formatDateDisplay(newEndForOriginal)}, new job starts ${formatDateDisplay(newStartForSplit)}`);
        }
      } else {
        await api.delete(`/jobs/${job.id}`);
        toast.success('Job deleted');
      }
      setDeleteJobDialog({ open: false, job: null });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update job'));
    } finally {
      setIsDeletingJob(false);
    }
  };

  // Build a job update payload for the Planning Board
  const buildPBJobUpdatePayload = (job) => ({
    customer_id: job.customer_id,
    contact_id: job.contact_id || null,
    site_id: job.site_id || '',
    site_contact_id: job.site_contact_id || null,
    one_off_street: job.one_off_street || '',
    one_off_suburb: job.one_off_suburb || '',
    one_off_state: job.one_off_state || '',
    one_off_postcode: job.one_off_postcode || '',
    equipment_type: job.equipment_type,
    attachment_type_ids: job.attachment_type_ids || [],
    worker_requirements: job.worker_requirements || [],
    start_date: job.start_date,
    start_time: job.start_time || '07:00',
    end_time: job.end_time || null,
    depot_time: job.depot_time || null,
    end_date: job.end_date,
    est_duration: job.est_duration != null ? job.est_duration : null,
    description: job.description || '',
    status: job.status,
    notes: job.notes || '',
    tags: job.tags || [],
    po_number: job.po_number || '',
    version: job.version,
  });

  // Click gantt bar to edit job - opens sheet instead of navigating away
  const handleBarClick = useCallback((job) => {
    if (!job?.id) return;
    setEditSheetJobId(job.id);
    setEditSheetOpen(true);
  }, []);

  const handleAddJobForAsset = useCallback((asset) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const prefill = { date: dateStr };
    if (asset?.equipment_type) prefill.equipmentType = asset.equipment_type;
    if (asset?.id) prefill.equipmentId = asset.id;
    setCreateSheetPrefill(prefill);
    setCreateSheetOpen(true);
  }, [currentDate]);

  // Format date according to tenant settings
  const formatDateDisplay = useCallback((dateString) => {
    if (!dateString) return '-';
    let date;
    if (typeof dateString === 'string') {
      date = dateString.includes('T') ? new Date(dateString) : new Date(dateString + 'T00:00:00');
    } else {
      date = dateString;
    }
    const dateFormat = tenantSettings.date_format || 'DD/MM/YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY': return `${day}-${monthShort}-${year}`;
      default: return `${day}/${month}/${year}`;
    }
  }, [tenantSettings.date_format]);

  // Re-read one job's crew for the board date (used when the backend split a crew POST across blocks)
  const refreshJobCrew = async (jobId) => {
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const { data } = await api.get(`/worker-allocations/job/${jobId}`);
      const forDay = (data || []).filter(wa => wa.is_active !== false && wa.start_date <= dateStr && (wa.end_date || wa.start_date) >= dateStr);
      setWorkerAllocations(prev => [...prev.filter(wa => wa.job_id !== jobId), ...forDay]);
    } catch { /* keep current board state */ }
  };

  // Fetch all data - using optimized V2 endpoint
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      
      // Use optimized V2 endpoint - single call replaces 10 API calls
      const [initRes, suppliersRes, dayDetailsRes, attachmentsRes, attTypesRes] = await Promise.all([
        api.get(`/v2/planning-board/init?date=${dateStr}`),
        api.get('/suppliers'),
        api.get(`/job-day-details?date=${dateStr}`),
        api.get('/attachments'),
        api.get('/attachment-types'),
      ]);
      
      const data = initRes.data;
      const dayDetails = dayDetailsRes.data || [];
      
      // Build a map of day overrides: job_id -> {start_time, est_duration}
      const dayOverrides = {};
      for (const dd of dayDetails) {
        dayOverrides[dd.job_id] = dd;
      }
      
      // Merge day overrides into jobs
      const mergedJobs = (data.jobs || []).map(j => {
        const override = dayOverrides[j.id];
        if (override) {
          return {
            ...j,
            _dayStartTime: override.start_time || null,
            _dayDuration: override.est_duration || null,
          };
        }
        return j;
      });
      
      setJobs(mergedJobs);
      setEquipment(data.equipment || []);
      setOperators(data.operators || []);
      setAllocations(data.allocations || []);
      setCustomers(data.customers || []);
      setSites(data.sites || []);
      setWorkerTypes(data.worker_types || []);
      setWorkerAllocations(data.worker_allocations || []);
      setEquipmentTypes(data.equipment_types || []);
      setSuppliers(suppliersRes.data || []);
      setAttachments(attachmentsRes.data || []);
      setAttachmentTypes(attTypesRes.data || []);
      setPendingLeaveWorkerIds(new Set(data.pending_leave_worker_ids || []));
      const pdlMap = {};
      for (const pdl of (data.part_day_leave || [])) {
        if (pdl.worker_id) pdlMap[pdl.worker_id] = pdl;
      }
      setPartDayLeaveMap(pdlMap);
      
      if (data.tenant_settings) {
        setTenantSettings({
          ...data.tenant_settings,
          date_format: data.tenant_settings.date_format || 'DD/MM/YYYY',
          time_format: data.tenant_settings.time_format || '24h',
          depot_time_enabled: data.tenant_settings.field_rules?.depot_time?.enabled ?? data.tenant_settings.depot_time_enabled ?? false,
          field_rules: data.tenant_settings.field_rules || {},
          relaxed_allocation: data.tenant_settings.relaxed_allocation ?? false,
        });
      }

      // Fetch allocation warnings (non-blocking)
      api.get(`/allocation-warnings?start_date=${dateStr}&end_date=${dateStr}`)
        .then(res => setAllocationWarnings(res.data || {}))
        .catch(() => setAllocationWarnings({}));
    } catch (error) {
      console.error('Error loading planning data:', error);
      toast.error('Failed to load planning data');
    } finally {
      setLoading(false);
    }
  }, [api, currentDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Scroll to current time on load
  useEffect(() => {
    if (!loading && timelineRef.current) {
      const now = new Date();
      const currentMinutes = (now.getHours() - TIMELINE_START_HOUR) * 60 + now.getMinutes();
      const scrollPosition = Math.max(0, (currentMinutes / 30) * SLOT_WIDTH - 200);
      timelineRef.current.scrollLeft = scrollPosition;
    }
  }, [loading]);

  // Get jobs for current date
  const jobsForDate = useMemo(() => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return jobs.filter(job => {
      // Dry-hire jobs live on the Dry Hire tab of the Schedule page.
      if (job.hire_type === 'dry') return false;
      const startDate = job.start_date;
      const endDate = job.end_date || job.start_date;
      if (startDate > dateStr || endDate < dateStr) return false;
      if (job.excluded_dates && job.excluded_dates.includes(dateStr)) return false;
      return true;
    }).sort((a, b) => {
      // Sort by job number only (stable order - doesn't jump around when start_time changes)
      return (a.job_number || 0) - (b.job_number || 0);
    });
  }, [jobs, currentDate]);

  // Group jobs by Asset Type → Asset → Jobs
  const assetGroupedJobs = useMemo(() => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');

    // --- Setup Group nesting: jobs that are children of a Setup Group will be
    // nested under their parent's row and hidden from their own asset lane.
    const groupChildrenByParentId = new Map(); // parentJobId -> [childJob, ...]
    const childJobIds = new Set();
    // Build a setup_group_id -> parent_job lookup across ALL jobsForDate
    const groupIdToParent = new Map();
    for (const j of jobsForDate) {
      if (j.setup_group_id && j.is_setup_parent) groupIdToParent.set(j.setup_group_id, j);
    }
    for (const j of jobsForDate) {
      if (j.setup_group_id && !j.is_setup_parent) {
        const parent = groupIdToParent.get(j.setup_group_id);
        if (parent) {
          childJobIds.add(j.id);
          if (!groupChildrenByParentId.has(parent.id)) groupChildrenByParentId.set(parent.id, []);
          groupChildrenByParentId.get(parent.id).push(j);
        }
      }
    }
    // Sort children by start_time for predictable ordering
    for (const arr of groupChildrenByParentId.values()) {
      arr.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    }

    // Step 1: Map jobs to their allocated asset (SKIP Setup Group children)
    const assetJobMap = new Map(); // assetId -> jobs[]
    const unassigned = [];

    for (const job of jobsForDate) {
      if (childJobIds.has(job.id)) continue; // rendered as nested sub-row under parent
      const allocation = allocations.find(a =>
        a.job_id === job.id &&
        a.start_date <= dateStr &&
        (a.end_date || a.start_date) >= dateStr
      );
      // Phase 2.1: if the main asset slot is empty but a prime mover is paired
      // (e.g. Truck towing a Semi-Trailer transport leg), group this job under
      // the prime mover's asset row so it doesn't look idle on the timeline.
      const assetId = allocation?.equipment_id || allocation?.prime_mover_equipment_id;
      if (assetId) {
        if (!assetJobMap.has(assetId)) assetJobMap.set(assetId, []);
        assetJobMap.get(assetId).push(job);
      } else {
        unassigned.push(job);
      }
    }

    // Step 2: Group ALL assets (busy + idle) by type
    const typeGroups = new Map(); // typeName -> { type, assets: [{ asset, jobs[] }] }
    const allAssets = equipment.filter(e => e.status !== 'decommissioned');
    for (const asset of allAssets) {
      const typeName = asset.equipment_type || 'Unknown';
      if (!typeGroups.has(typeName)) {
        typeGroups.set(typeName, { type: typeName, assets: [] });
      }
      typeGroups.get(typeName).assets.push({
        asset,
        jobs: assetJobMap.get(asset.id) || [],
      });
    }

    // Sort assets within each type: busy first (by asset_id), then idle (by asset_id)
    for (const group of typeGroups.values()) {
      group.assets.sort((a, b) => {
        const aHasJobs = a.jobs.length > 0 ? 0 : 1;
        const bHasJobs = b.jobs.length > 0 ? 0 : 1;
        if (aHasJobs !== bHasJobs) return aHasJobs - bHasJobs;
        return (a.asset.asset_id || '').localeCompare(b.asset.asset_id || '');
      });
    }

    // Hide unallocated (idle) assets when toggle is off
    if (!showUnallocatedAssets) {
      for (const group of typeGroups.values()) {
        group.assets = group.assets.filter(a => a.jobs.length > 0);
      }
    }

    // Sort type groups alphabetically, remove empty groups
    let result = Array.from(typeGroups.values())
      .filter(g => showUnallocatedAssets || g.assets.length > 0)
      .sort((a, b) => a.type.localeCompare(b.type));

    // Apply asset type filter
    if (timelineAssetTypeFilter.length > 0) {
      result = result.filter(g => timelineAssetTypeFilter.includes(g.type));
    }

    return { typeGroups: result, unassigned: timelineAssetTypeFilter.length > 0 ? [] : unassigned, groupChildrenByParentId };
  }, [jobsForDate, allocations, equipment, currentDate, timelineAssetTypeFilter, showUnallocatedAssets]);

  // Get allocation for a job on current date
  const getJobAllocation = useCallback((jobId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.find(a => 
      a.job_id === jobId && 
      a.start_date <= dateStr && 
      (a.end_date || a.start_date) >= dateStr
    );
  }, [allocations, currentDate]);

  // ================== ALLOCATION-BLOCK HELPERS (multi-day jobs) ==================
  // Jobs whose crew are owned by allocation blocks; single-day, tower crane and labour-only keep old behaviour
  const isBlockJob = (job) => !!(job && job.end_date && job.end_date > job.start_date && !job.is_tower_crane && job.equipment_type !== 'No Asset');
  const addDaysStr = (ds, n) => {
    const [y, m, d] = ds.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().split('T')[0];
  };
  const coversOnlyDay = (block, dateStr) => block.start_date === dateStr && (block.end_date || block.start_date) === dateStr;
  const willSplit = (block, dateStr) => !!(block && !block.is_support_asset && !coversOnlyDay(block, dateStr));
  const boardDayLabel = () => format(currentDate, 'EEE d MMM');
  const getDayBlock = (jobId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.find(a => a.job_id === jobId && !a.is_support_asset && a.start_date <= dateStr && (a.end_date || a.start_date) >= dateStr);
  };

  // Split the board's day off its block (POST /allocations/{id}/isolate-day). Throws on failure (already toasted).
  const isolateDayBlock = async (block, dateStr) => {
    if (!willSplit(block, dateStr)) return block?.id;
    try {
      const { data } = await api.post(`/allocations/${block.id}/isolate-day`, { date: dateStr });
      setAllocations(prev => prev.map(a => a.id === block.id ? { ...a, start_date: dateStr, end_date: dateStr } : a));
      setWorkerAllocations(prev => prev.map(wa =>
        wa.allocation_id === block.id && wa.start_date <= dateStr && (wa.end_date || wa.start_date) >= dateStr
          ? { ...wa, start_date: dateStr, end_date: dateStr } : wa));
      return data.allocation_id;
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not split the allocation block'));
      err.isolateToasted = true;
      throw err;
    }
  };

  // Leave preflight → resolves with the override reason, '' if no leave clash, or null if cancelled
  const confirmLeave = async (items) => {
    let blocks = [];
    try {
      const { data } = await api.post('/allocations/leave-preflight', { items });
      blocks = data?.blocks || [];
    } catch { /* preflight failed — proceed */ }
    if (blocks.length === 0) return '';
    return new Promise(resolve => setLeaveOverride({
      open: true, blocks, callback: (reason) => resolve(reason), onCancel: () => resolve(null),
    }));
  };
  const getCustomerInfo = useCallback((customerId) => {
    return customers.find(c => c.id === customerId);
  }, [customers]);

  // ================== MEMOIZED LOOKUP MAPS ==================
  // These replace repeated .find() calls with O(1) Map lookups for performance
  const jobsMap = useMemo(() => new Map(jobs.map(j => [j.id, j])), [jobs]);
  const equipmentMap = useMemo(() => new Map(equipment.map(e => [e.id, e])), [equipment]);
  const operatorsMap = useMemo(() => new Map(operators.map(o => [o.id, o])), [operators]);
  const customersMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
  const sitesMap = useMemo(() => new Map(sites.map(s => [s.id, s])), [sites]);
  const workerTypesMap = useMemo(() => new Map(workerTypes.map(w => [w.id, w])), [workerTypes]);
  const suppliersMap = useMemo(() => new Map(suppliers.map(s => [s.id, s])), [suppliers]);

  // Get equipment info - now uses memoized Map for O(1) lookup
  const getEquipmentInfo = useCallback((equipmentId) => {
    return equipmentMap.get(equipmentId);
  }, [equipmentMap]);

  // Get operator info - now uses memoized Map for O(1) lookup
  const getOperatorInfo = useCallback((operatorId) => {
    return operatorsMap.get(operatorId);
  }, [operatorsMap]);

  // Get worker type name - now uses memoized Map for O(1) lookup
  const getWorkerTypeName = useCallback((workerTypeId) => {
    const wt = workerTypesMap.get(workerTypeId);
    return wt?.name || 'Worker';
  }, [workerTypesMap]);

  // Get worker type info (name and color) - now uses memoized Map for O(1) lookup
  const getWorkerTypeInfo = useCallback((workerTypeId) => {
    const wt = workerTypesMap.get(workerTypeId);
    return {
      name: wt?.name || 'Worker',
      color: wt?.color || '#8B5CF6'
    };
  }, [workerTypesMap]);

  // Get supplier name - now uses memoized Map for O(1) lookup
  const getSupplierName = useCallback((supplierId) => {
    if (!supplierId) return null;
    const supplier = suppliersMap.get(supplierId);
    return supplier?.company_name || null;
  }, [suppliersMap]);

  // Get worker allocations for a job on current date
  const getJobWorkerAllocations = useCallback((jobId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return workerAllocations.filter(wa => 
      wa.job_id === jobId && 
      wa.start_date <= dateStr && 
      (wa.end_date || wa.start_date) >= dateStr
    );
  }, [workerAllocations, currentDate]);

  // Check if asset is allocated for current date
  // Note: includes prime-mover pairings — an asset serving as the prime mover
  // on another job (Phase 2.1) is equally "in use".
  const isAssetAllocated = useCallback((assetId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.some(a => {
      if (!(a.start_date <= dateStr && (a.end_date || a.start_date) >= dateStr)) return false;
      if (a.equipment_id === assetId || a.prime_mover_equipment_id === assetId) return true;
      if ((a.additional_trailer_equipment_ids || []).includes(assetId)) return true;
      // Combo reservation: a member of an allocated combo asset is in use too.
      const eq = a.equipment_id ? equipmentMap.get(a.equipment_id) : null;
      if ((eq?.combo_members || []).some(m => m?.equipment_id === assetId)) return true;
      return false;
    });
  }, [allocations, currentDate, equipmentMap]);

  // Check if an asset is currently riding on a trailer as cargo (Trailer Load P2).
  // Rendered as a dimmer "In Transit" pill instead of "In Use".
  const isAssetInTransit = useCallback((assetId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.some(a =>
      (a.cargo_equipment_ids || []).includes(assetId) &&
      a.start_date <= dateStr &&
      (a.end_date || a.start_date) >= dateStr
    );
  }, [allocations, currentDate]);

  // Check if operator is allocated for current date (includes worker allocations)
  const isOperatorAllocated = useCallback((operatorId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    // Check main allocations
    const mainAllocation = allocations.some(a => 
      a.operator_id === operatorId && 
      a.start_date <= dateStr && 
      (a.end_date || a.start_date) >= dateStr
    );
    if (mainAllocation) return true;
    
    // Check worker allocations
    const workerAllocation = workerAllocations.some(wa => 
      wa.operator_id === operatorId && 
      wa.start_date <= dateStr && 
      (wa.end_date || wa.start_date) >= dateStr
    );
    return workerAllocation;
  }, [allocations, workerAllocations, currentDate]);

  // Helper to un-confirm a job when changes are made - now uses memoized Map
  const unconfirmJobIfNeeded = useCallback(async (jobId, newStatus = 'allocated') => {
    const job = jobsMap.get(jobId);
    if (!job || !['confirmed', 'mixed', 'allocated'].includes(job.status)) return false;
    
    // Optimistic update
    setJobs(prev => prev.map(j => 
      j.id === jobId ? { ...j, status: newStatus } : j
    ));
    
    try {
      await api.patch(`/jobs/${jobId}`, { status: newStatus });
      toast.info(`Job un-confirmed due to allocation change (now ${newStatus.replace('_', ' ')})`);
      return true;
    } catch (error) {
      // Rollback on error
      setJobs(prev => prev.map(j => 
        j.id === jobId ? { ...j, status: 'confirmed' } : j
      ));
      console.error('Failed to un-confirm job:', error);
      return false;
    }
  }, [jobsMap, api]);

  // After any allocation/worker change, the backend is the single source of
  // truth for the job's derived status. Refetch it so the Planning Board badge
  // matches the Schedule list view (no client-side guessing / no drift).
  const syncJobStatus = useCallback(async (jobId) => {
    if (!jobId) return;
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      if (data && data.status) {
        setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: data.status } : j));
      }
    } catch (e) {
      // non-fatal — optimistic value stays until next full refresh
    }
  }, [api]);


  // Helper to get "Day X of Y" for multi-day jobs
  const getJobDayInfo = useCallback((job) => {
    if (!job.end_date || job.end_date === job.start_date) {
      return null; // Not a multi-day job
    }
    
    try {
      const start = parseISO(job.start_date);
      const end = parseISO(job.end_date);
      const allDays = eachDayOfInterval({ start, end });
      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
      
      const dayIndex = allDays.findIndex(d => format(d, 'yyyy-MM-dd') === currentDateStr);
      
      if (dayIndex === -1) return null;
      
      return {
        dayNumber: dayIndex + 1,
        totalDays: allDays.length
      };
    } catch (e) {
      return null;
    }
  }, [currentDate]);

  // Filter assets
  const filteredAssets = useMemo(() => {
    // Include all active assets (exclude only out_of_service)
    let filtered = equipment.filter(e => 
      e.status !== 'out_of_service'
    );
    
    // Filter by selected asset types (if any selected)
    if (selectedAssetTypes.length > 0) {
      filtered = filtered.filter(e => selectedAssetTypes.includes(e.equipment_type));
    }
    
    if (assetSearch) {
      const search = assetSearch.toLowerCase();
      filtered = filtered.filter(e => 
        (e.asset_id || '').toLowerCase().includes(search) ||
        (e.name || '').toLowerCase().includes(search) ||
        (e.equipment_type || '').toLowerCase().includes(search)
      );
    }
    
    if (!showAllocatedAssets) {
      filtered = filtered.filter(e => !isAssetAllocated(e.id));
    }
    
    return filtered;
  }, [equipment, assetSearch, showAllocatedAssets, isAssetAllocated, allocations, selectedAssetTypes]);

  // Trailer Load (P2) — filtered attachments for the sidebar
  const filteredAttachments = useMemo(() => {
    let filtered = (attachments || []).filter(a => a.status !== 'retired' && a.status !== 'out_of_service');
    if (attachmentSearch) {
      const s = attachmentSearch.toLowerCase();
      filtered = filtered.filter(a =>
        (a.name || '').toLowerCase().includes(s) ||
        (a.asset_id || '').toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [attachments, attachmentSearch]);

  const isAttachmentInTransit = useCallback((attachmentId) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return allocations.some(a =>
      (a.cargo_attachment_ids || []).includes(attachmentId) &&
      a.start_date <= dateStr &&
      (a.end_date || a.start_date) >= dateStr
    );
  }, [allocations, currentDate]);

  const getAttachmentTypeName = useCallback((typeId) => {
    if (!typeId) return null;
    return attachmentTypes.find(t => t.id === typeId)?.name || null;
  }, [attachmentTypes]);

  // Get unique asset types for filter
  const assetTypes = useMemo(() => {
    const types = new Set();
    equipment.forEach(e => {
      if (e.equipment_type) types.add(e.equipment_type);
    });
    return Array.from(types).sort();
  }, [equipment]);

  // Filter operators
  const filteredOperators = useMemo(() => {
    // Include operators that are active or have no status set (legacy data)
    // Exclude only explicitly inactive/terminated operators
    let filtered = operators.filter(o => 
      !o.status || o.status === 'active' || o.status === 'available'
    );
    
    // Filter by selected worker types (if any selected)
    if (selectedWorkerTypes.length > 0) {
      filtered = filtered.filter(o => {
        const operatorWorkerTypes = o.worker_type_ids || [];
        return selectedWorkerTypes.some(wtId => operatorWorkerTypes.includes(wtId));
      });
    }
    
    if (operatorSearch) {
      const search = operatorSearch.toLowerCase();
      filtered = filtered.filter(o => 
        (o.name || '').toLowerCase().includes(search) ||
        (o.email || '').toLowerCase().includes(search)
      );
    }
    
    if (!showAllocatedOperators) {
      filtered = filtered.filter(o => !isOperatorAllocated(o.id));
    }
    
    return filtered;
  }, [operators, operatorSearch, showAllocatedOperators, isOperatorAllocated, workerAllocations, allocations, selectedWorkerTypes]);

  // Handle date navigation
  const navigateDate = (direction) => {
    setCurrentDate(prev => direction === 'prev' ? subDays(prev, 1) : addDays(prev, 1));
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveData(active.data.current);
    setIsDragging(true);
  };

  // ---- Allocation helpers (extracted for multi-day prompt reuse) ----

  // Apply standard allocation (asset or operator to a job)
  // EW2-311: auto-assign the asset's designated operator onto one block (block-job All Days path)
  const autoAssignDesignatedOperator = async (jobId, allocationId, equipmentId, dateStr) => {
    try {
      const { data: opCheck } = await api.get(`/equipment/${equipmentId}/designated-operator-availability?date=${dateStr}`);
      if (opCheck.designated_operator && opCheck.available) {
        await api.patch(`/allocations/${allocationId}?skip_validation=true`, { operator_id: opCheck.designated_operator.id });
        toast.success(`Auto-assigned designated operator: ${opCheck.designated_operator.name}`);
        await syncJobStatus(jobId);
      } else if (opCheck.designated_operator && !opCheck.available) {
        const reasons = { already_allocated: 'already allocated to another job', on_leave: 'on leave', inactive: 'inactive' };
        toast.warning(`Designated operator ${opCheck.designated_operator.name} is ${reasons[opCheck.reason] || 'unavailable'} on this date`, { duration: 6000 });
      }
    } catch (_) { /* non-fatal */ }
  };

  // Blocks that belong to the same asset slot as `targetBlock`; null when the slot can't be told apart
  const blocksOverlap = (list) => list.some((a, i) => list.some((b, j) =>
    j > i && a.start_date <= (b.end_date || b.start_date) && b.start_date <= (a.end_date || a.start_date)));
  const getSlotBlocks = (job, allBlocks, targetBlock) => {
    const target = targetBlock && allBlocks.find(b => b.id === targetBlock.id);
    const overlapping = blocksOverlap(allBlocks);
    if (!target) {
      const reqQty = (job.asset_requirements || []).reduce((n, r) => n + (Number(r.quantity) || 1), 0);
      return (reqQty > 1 || overlapping) ? null : allBlocks;
    }
    if (target.asset_requirement_id) {
      const idx = target.asset_requirement_index ?? 0;
      return allBlocks.filter(b => b.asset_requirement_id === target.asset_requirement_id && (b.asset_requirement_index ?? 0) === idx);
    }
    if (overlapping) {
      return target.equipment_id
        ? allBlocks.filter(b => !b.asset_requirement_id && b.equipment_id === target.equipment_id)
        : [target];
    }
    return allBlocks;
  };

  // Block job, All Days: set the resource on every block (never changing dates) and fill uncovered days.
  // Returns false when the job has no blocks yet, so the caller keeps the single-POST path.
  const applyAllDaysToBlocks = async (jobId, job, resourceType, resourceId, drop, reason, targetBlock) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    let allBlocks;
    try {
      const { data } = await api.get(`/jobs/${jobId}/allocations`);
      allBlocks = (data || []).filter(b => !b.is_support_asset);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load allocation blocks'));
      return true;
    }
    if (allBlocks.length === 0) return false;

    // Only the blocks for the dropped-on asset slot change (multi-asset jobs)
    const blocks = getSlotBlocks(job, allBlocks, targetBlock);
    if (!blocks) {
      toast.error('This job has more than one asset. Use the multi-day allocations window to change all days.');
      return true;
    }
    const target = targetBlock && allBlocks.find(b => b.id === targetBlock.id);
    const slotFields = target?.asset_requirement_id
      ? { asset_requirement_id: target.asset_requirement_id, asset_requirement_index: target.asset_requirement_index ?? 0 }
      : {};
    if (job.status === 'confirmed') await unconfirmJobIfNeeded(jobId, 'allocated');

    const updateFor = (b) => {
      if (resourceType === 'operator') return { operator_id: resourceId };
      if (drop.isTrailerSlotDrop) {
        const cur = (b.additional_trailer_equipment_ids || []).filter(Boolean);
        return cur.includes(resourceId) ? null : { additional_trailer_equipment_ids: [...cur, resourceId] };
      }
      if (drop.isPrimeMoverDrop) return { prime_mover_equipment_id: resourceId };
      return { equipment_id: resourceId };
    };
    try {
      for (const b of blocks) {
        const upd = updateFor(b);
        if (upd) await api.patch(withLeaveOverride(`/allocations/${b.id}?skip_validation=true`, reason), upd);
      }
      // One new block per run of days that no block covers
      const covered = (d) => blocks.some(b => b.start_date <= d && (b.end_date || b.start_date) >= d);
      const runs = [];
      for (let d = job.start_date; d <= job.end_date; d = addDaysStr(d, 1)) {
        if (covered(d)) continue;
        const last = runs[runs.length - 1];
        if (last && last[1] === addDaysStr(d, -1)) last[1] = d; else runs.push([d, d]);
      }
      const created = [];
      for (const [start, end] of runs) {
        const { data: nb } = await api.post(withLeaveOverride('/allocations?skip_validation=true', reason), {
          job_id: jobId, start_date: start, end_date: end,
          equipment_id: (resourceType === 'equipment' && !drop.isPrimeMoverDrop) ? resourceId : '',
          operator_id: resourceType === 'operator' ? resourceId : '',
          prime_mover_equipment_id: drop.isPrimeMoverDrop ? resourceId : null,
          ...slotFields,
        });
        if (nb?.id) created.push(nb);
      }
      toast.success(`${drop.label} assigned for all days`);
      if (resourceType === 'equipment' && !drop.isPrimeMoverDrop && !drop.isTrailerSlotDrop) {
        const dayBlock = [...blocks, ...created].find(b => b.start_date <= dateStr && (b.end_date || b.start_date) >= dateStr);
        if (dayBlock && !dayBlock.operator_id) await autoAssignDesignatedOperator(jobId, dayBlock.id, resourceId, dateStr);
      }
      await syncJobStatus(jobId);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to allocate'));
    }
    fetchData();
    return true;
  };

  const applyStandardAllocation = async (jobId, job, resourceType, resourceId, scope, leaveOverrideReason = null) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const isAllDays = scope === 'all';
    const allocStartDate = isAllDays ? job.start_date : dateStr;
    const allocEndDate = isAllDays ? job.end_date : (job.end_date && job.end_date !== job.start_date ? dateStr : null);
    const existingAllocation = getJobAllocation(jobId);

    // --- Asset type validation: block mismatched equipment types ---
    if (resourceType === 'equipment' && job.equipment_type) {
      const draggedAsset = equipment.find(e => e.id === resourceId);
      if (draggedAsset) {
        const jobEqType = equipmentTypes.find(t => t.name === job.equipment_type);
        const isPrimeMoverType = jobEqType?.requires_prime_mover && jobEqType?.prime_mover_asset_type_id &&
          draggedAsset.equipment_type === equipmentTypes.find(t => t.id === jobEqType.prime_mover_asset_type_id)?.name;
        if (!tenantSettings.relaxed_allocation && !isPrimeMoverType && draggedAsset.equipment_type !== job.equipment_type) {
          toast.error(`Asset does not match Asset Type required. Job requires "${job.equipment_type}" but "${draggedAsset.asset_id || draggedAsset.name}" is "${draggedAsset.equipment_type}".`);
          return;
        }
      }
    }

    // --- Primary operator validation: block workers who don't operate the required asset type ---
    if (!tenantSettings.relaxed_allocation && resourceType === 'operator' && job.equipment_type) {
      const operator = operators.find(o => o.id === resourceId);
      if (operator) {
        const opEqTypes = operator.equipment_types || [];
        if (opEqTypes.length === 0) {
          toast.error(`Worker ${operator.name} is not set to operate any asset types. Job requires "${job.equipment_type}".`);
          return;
        }
        if (!opEqTypes.includes(job.equipment_type)) {
          toast.error(`Worker ${operator.name} does not operate "${job.equipment_type}". They operate: ${opEqTypes.join(', ')}.`);
          return;
        }
      }
    }

    // --- Mobilisation Phase 2.1: smart routing to prime_mover_equipment_id ---
    // If job's equipment_type requires a prime mover AND the dragged asset matches the
    // configured prime-mover asset type, route to prime_mover_equipment_id instead of equipment_id.
    let isPrimeMoverDrop = false;
    // --- Multi-trailer rigs (B-Double / Road Train): route extra trailers to
    // the next empty additional_trailer_equipment_ids slot ---
    let isTrailerSlotDrop = false;
    let nextTrailerArray = null;
    if (resourceType === 'equipment') {
      const jobEqType = equipmentTypes.find(t => t.name === job.equipment_type);
      if (jobEqType?.requires_prime_mover && jobEqType?.prime_mover_asset_type_id) {
        const draggedAsset = equipment.find(e => e.id === resourceId);
        const pmTypeName = equipmentTypes.find(t => t.id === jobEqType.prime_mover_asset_type_id)?.name;
        if (draggedAsset?.equipment_type === pmTypeName) {
          isPrimeMoverDrop = true;
        } else {
          // Dropped asset is a trailer-type unit. If this is a multi-trailer rig
          // and the primary trailer slot is already filled, fill the next empty
          // additional trailer slot.
          const trailerCount = (jobEqType?.trailer_count || 1);
          if (trailerCount > 1 && existingAllocation && existingAllocation.equipment_id) {
            const current = (existingAllocation.additional_trailer_equipment_ids || []).filter(Boolean);
            // Skip if this asset is already on the rig
            if (existingAllocation.equipment_id === resourceId ||
                existingAllocation.prime_mover_equipment_id === resourceId ||
                current.includes(resourceId)) {
              toast.info('That trailer is already on this rig');
              return;
            }
            if (current.length >= trailerCount - 1) {
              toast.info(`All ${trailerCount} trailer slots are already filled`);
              return;
            }
            isTrailerSlotDrop = true;
            nextTrailerArray = [...current, resourceId];
          }
        }
      }
    }

    // Leave-preflight for operator drops (before any state changes)
    if (resourceType === 'operator' && !leaveOverrideReason) {
      try {
        const { data: pf } = await api.post('/allocations/leave-preflight', {
          items: [{ operator_id: resourceId, start_date: allocStartDate, end_date: allocEndDate || allocStartDate }],
        });
        if (pf?.blocks?.length > 0) {
          setLeaveOverride({
            open: true,
            blocks: pf.blocks,
            callback: (reason) => applyStandardAllocation(jobId, job, resourceType, resourceId, scope, reason),
          });
          return;
        }
      } catch { /* preflight failed — proceed */ }
    }

    // Multi-day jobs with allocation blocks: changes stay inside their blocks (un-confirms inside, before writing)
    const blockMode = isBlockJob(job);
    const dropLabel = isTrailerSlotDrop ? 'Trailer' : (isPrimeMoverDrop ? 'Prime mover' : (resourceType === 'equipment' ? 'Asset' : 'Operator'));
    if (blockMode && isAllDays &&
        await applyAllDaysToBlocks(jobId, job, resourceType, resourceId, { isPrimeMoverDrop, isTrailerSlotDrop, label: dropLabel },
          leaveOverrideReason, existingAllocation?.is_support_asset ? null : existingAllocation)) {
      return;
    }

    // Un-confirm the job if it was confirmed (allocation change)
    if (job.status === 'confirmed') {
      await unconfirmJobIfNeeded(jobId, 'allocated');
    }

    try {
      let splitNote = '';
      if (existingAllocation && blockMode && !isAllDays && willSplit(existingAllocation, dateStr)) {
        // Split this day off the block first; the day keeps the block's id
        await isolateDayBlock(existingAllocation, dateStr);
        splitNote = ` for ${boardDayLabel()} (block split for this day)`;
      }
      if (existingAllocation) {
        // Optimistic update for existing allocation
        const updateData = {};
        if (resourceType === 'equipment') {
          if (isTrailerSlotDrop) {
            updateData.additional_trailer_equipment_ids = nextTrailerArray;
          } else if (isPrimeMoverDrop) {
            updateData.prime_mover_equipment_id = resourceId;
          } else {
            updateData.equipment_id = resourceId;
          }
        } else if (resourceType === 'operator') {
          updateData.operator_id = resourceId;
        }
        // If "all days", also expand existing allocation's date range (non-block jobs only)
        if (isAllDays && !blockMode) {
          updateData.start_date = allocStartDate;
          updateData.end_date = allocEndDate;
        }
        
        // Update local allocation state immediately
        setAllocations(prev => prev.map(a => 
          a.id === existingAllocation.id ? { ...a, ...updateData } : a
        ));
        
        // Determine new job status based on whether both asset + operator are now present
        const updatedAlloc = { ...existingAllocation, ...updateData };
        const hasBoth = Boolean(updatedAlloc.equipment_id) && Boolean(updatedAlloc.operator_id);
        const newJobStatus = hasBoth ? 'allocated' : 'partially_allocated';
        setJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, status: newJobStatus } : j
        ));
        
        const label = isTrailerSlotDrop ? `Trailer ${nextTrailerArray.length + 1}` : (isPrimeMoverDrop ? 'Prime mover' : (resourceType === 'equipment' ? 'Asset' : 'Operator'));
        toast.success(`${label} assigned${isAllDays ? ' for all days' : splitNote}`);

        // Sync with server (skip validation - Planning Board allows double-booking)
        await api.patch(withLeaveOverride(`/allocations/${existingAllocation.id}?skip_validation=true`, leaveOverrideReason), updateData);
        await syncJobStatus(jobId);

        // EW2-311: Auto-assign designated operator when equipment is dropped and no operator set
        if (resourceType === 'equipment' && !isPrimeMoverDrop && !isTrailerSlotDrop && !updatedAlloc.operator_id) {
          try {
            const { data: opCheck } = await api.get(`/equipment/${resourceId}/designated-operator-availability?date=${allocStartDate}`);
            if (opCheck.designated_operator && opCheck.available) {
              await api.patch(`/allocations/${existingAllocation.id}?skip_validation=true`, { operator_id: opCheck.designated_operator.id });
              setAllocations(prev => prev.map(a => a.id === existingAllocation.id ? { ...a, operator_id: opCheck.designated_operator.id } : a));
              toast.success(`Auto-assigned designated operator: ${opCheck.designated_operator.name}`);
              await syncJobStatus(jobId);
            } else if (opCheck.designated_operator && !opCheck.available) {
              const reasons = { already_allocated: 'already allocated to another job', on_leave: 'on leave', inactive: 'inactive' };
              toast.warning(`Designated operator ${opCheck.designated_operator.name} is ${reasons[opCheck.reason] || 'unavailable'} on this date`, { duration: 6000 });
            }
          } catch (_) { /* non-fatal */ }
        }
      } else {
        // Create new allocation with optimistic update
        const tempId = `temp-${Date.now()}`;
        const newAllocation = {
          id: tempId,
          job_id: jobId,
          start_date: allocStartDate,
          end_date: allocEndDate,
          equipment_id: (resourceType === 'equipment' && !isPrimeMoverDrop) ? resourceId : '',
          operator_id: resourceType === 'operator' ? resourceId : '',
          prime_mover_equipment_id: isPrimeMoverDrop ? resourceId : null,
        };
        
        setAllocations(prev => [...prev, newAllocation]);
        
        const newJobStatus = (newAllocation.equipment_id && newAllocation.operator_id) ? 'allocated' : 'partially_allocated';
        setJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, status: newJobStatus } : j
        ));
        
        const label = isPrimeMoverDrop ? 'Prime mover' : (resourceType === 'equipment' ? 'Asset' : 'Operator');
        toast.success(`${label} allocated${isAllDays ? ' for all days' : ''}`);

        // Sync with server (skip validation - Planning Board allows double-booking)
        const response = await api.post(withLeaveOverride('/allocations?skip_validation=true', leaveOverrideReason), newAllocation);
        
        // Replace temp with real allocation
        setAllocations(prev => 
          prev.map(a => a.id === tempId ? response.data : a)
        );
        await syncJobStatus(jobId);

        // EW2-311: Auto-assign designated operator when equipment is dropped (new allocation)
        if (resourceType === 'equipment' && !isPrimeMoverDrop && response.data?.id) {
          try {
            const { data: opCheck } = await api.get(`/equipment/${resourceId}/designated-operator-availability?date=${allocStartDate}`);
            if (opCheck.designated_operator && opCheck.available) {
              await api.patch(`/allocations/${response.data.id}?skip_validation=true`, { operator_id: opCheck.designated_operator.id });
              setAllocations(prev => prev.map(a => a.id === response.data.id ? { ...a, operator_id: opCheck.designated_operator.id } : a));
              toast.success(`Auto-assigned designated operator: ${opCheck.designated_operator.name}`);
              await syncJobStatus(jobId);
            } else if (opCheck.designated_operator && !opCheck.available) {
              const reasons = { already_allocated: 'already allocated to another job', on_leave: 'on leave', inactive: 'inactive' };
              toast.warning(`Designated operator ${opCheck.designated_operator.name} is ${reasons[opCheck.reason] || 'unavailable'} on this date`, { duration: 6000 });
            }
          } catch (_) { /* non-fatal */ }
        }
      }
    } catch (error) {
      if (!error.isolateToasted) toast.error(getApiErrorMessage(error, 'Failed to allocate'));
      fetchData();
    }
  };

  // Block job, one day: swap the crew member on this day only (split the block first)
  const swapCrewBlockDay = async (jobId, existing, resourceId, labels) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const revert = () => setWorkerAllocations(prev => prev.map(wa => wa.id === existing.id ? { ...wa, operator_id: existing.operator_id } : wa));
    setWorkerAllocations(prev => prev.map(wa => wa.id === existing.id ? { ...wa, operator_id: resourceId } : wa));
    const waEnd = existing.end_date || existing.start_date;
    const unlinkedSpan = !existing.allocation_id && (existing.start_date < dateStr || waEnd > dateStr);
    // One leave check before any write: the new worker for the day, plus (unlinked rows) the old
    // worker's before/after pieces, which are re-created
    const leaveItems = [{ operator_id: resourceId, start_date: dateStr, end_date: dateStr }];
    if (unlinkedSpan && existing.start_date < dateStr) {
      leaveItems.push({ operator_id: existing.operator_id, start_date: existing.start_date, end_date: addDaysStr(dateStr, -1) });
    }
    if (unlinkedSpan && waEnd > dateStr) {
      leaveItems.push({ operator_id: existing.operator_id, start_date: addDaysStr(dateStr, 1), end_date: waEnd });
    }
    const reason = await confirmLeave(leaveItems);
    if (reason === null) { revert(); return; }
    if (labels.job?.status === 'confirmed') await unconfirmJobIfNeeded(jobId, 'allocated');
    const url = withLeaveOverride(`/worker-allocations/${existing.id}`, reason);
    try {
      let split = false;
      if (existing.allocation_id) {
        const block = allocations.find(a => a.id === existing.allocation_id) || getDayBlock(jobId);
        split = willSplit(block, dateStr);
        await isolateDayBlock(block, dateStr);
        await api.patch(url, { operator_id: resourceId });
      } else if (unlinkedSpan) {
        // Unlinked row spanning other days: keep the old worker on the other days
        const block = getDayBlock(jobId);
        split = willSplit(block, dateStr);
        const dayBlockId = block ? await isolateDayBlock(block, dateStr) : null;
        const base = { job_id: jobId, worker_type_id: existing.worker_type_id, operator_id: existing.operator_id, allocation_id: null };
        const pieceUrl = withLeaveOverride('/worker-allocations', reason);
        if (existing.start_date < dateStr) {
          await api.post(pieceUrl, { ...base, start_date: existing.start_date, end_date: addDaysStr(dateStr, -1) });
        }
        if (waEnd > dateStr) {
          await api.post(pieceUrl, { ...base, start_date: addDaysStr(dateStr, 1), end_date: waEnd });
        }
        await api.patch(url, { operator_id: resourceId, start_date: dateStr, end_date: dateStr, ...(dayBlockId ? { allocation_id: dayBlockId } : {}) });
        setWorkerAllocations(prev => prev.map(wa => wa.id === existing.id
          ? { ...wa, start_date: dateStr, end_date: dateStr, allocation_id: dayBlockId || wa.allocation_id } : wa));
      } else {
        await api.patch(url, { operator_id: resourceId });
      }
      toast.success(`${labels.operatorName} assigned as ${labels.workerTypeName} for ${boardDayLabel()}${split ? ' (block split for this day)' : ''}`);
      await syncJobStatus(jobId);
    } catch (e) {
      revert();
      if (!e.isolateToasted) toast.error(getApiErrorMessage(e, 'Failed to update worker assignment'));
      fetchData();
    }
  };

  // Block job, All Days: swap that worker on every crew row of this type, never touching dates
  const swapCrewBlockAllDays = async (jobId, existing, resourceId, labels) => {
    try {
      const { data } = await api.get(`/worker-allocations/job/${jobId}`);
      const rows = (data || []).filter(wa => wa.is_active !== false &&
        wa.worker_type_id === existing.worker_type_id && wa.operator_id === existing.operator_id);
      if (rows.length === 0) return;
      const reason = await confirmLeave(rows.map(r => ({ operator_id: resourceId, start_date: r.start_date, end_date: r.end_date || r.start_date })));
      if (reason === null) return;
      if (labels.job?.status === 'confirmed') await unconfirmJobIfNeeded(jobId, 'allocated');
      for (const r of rows) {
        await api.patch(withLeaveOverride(`/worker-allocations/${r.id}`, reason), { operator_id: resourceId });
      }
      toast.success(`${labels.operatorName} replaced previous operator as ${labels.workerTypeName} for all days`);
      await syncJobStatus(jobId);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to update worker assignment'));
    }
    fetchData();
  };

  // Block job, one day: fill an empty crew slot on the day block (split first)
  const fillCrewBlockDay = async (jobId, workerTypeId, resourceId, labels) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const reason = await confirmLeave([{ operator_id: resourceId, start_date: dateStr, end_date: dateStr }]);
    if (reason === null) return;
    if (labels.job?.status === 'confirmed') await unconfirmJobIfNeeded(jobId, 'allocated');
    try {
      const block = getDayBlock(jobId);
      const split = willSplit(block, dateStr);
      const dayBlockId = block ? await isolateDayBlock(block, dateStr) : null;
      const { data } = await api.post(withLeaveOverride('/worker-allocations', reason), {
        job_id: jobId, worker_type_id: workerTypeId, operator_id: resourceId,
        start_date: dateStr, end_date: dateStr,
        ...(dayBlockId ? { allocation_id: dayBlockId } : {}),
      });
      setWorkerAllocations(prev => [...prev, data]);
      toast.success(`${labels.operatorName} assigned as ${labels.workerTypeName} for ${boardDayLabel()}${split ? ' (block split for this day)' : ''}`);
      await syncJobStatus(jobId);
    } catch (e) {
      if (!e.isolateToasted) toast.error(getApiErrorMessage(e, 'Failed to assign worker'));
      fetchData();
    }
  };

  // Apply worker slot allocation (operator to a worker type slot)
  const applyWorkerSlotAllocation = async (jobId, job, resourceId, workerTypeId, existingAllocationId, scope) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const isAllDays = scope === 'all';
    const workerTypeName = getWorkerTypeName(workerTypeId);
    const operatorName = operators.find(o => o.id === resourceId)?.name || 'Operator';

    // --- Worker type validation: block workers without the required type ---
    if (!tenantSettings.relaxed_allocation && workerTypeId) {
      const operator = operators.find(o => o.id === resourceId);
      const opWorkerTypes = operator?.worker_type_ids || [];
      if (!opWorkerTypes.includes(workerTypeId)) {
        toast.error(`Worker ${operatorName} does not match Worker Type ${workerTypeName}.`);
        return;
      }
    }

    // Multi-day jobs with allocation blocks: changes stay inside their block.
    // Un-confirming the job happens after the leave check, so cancelling writes nothing.
    if (isBlockJob(job)) {
      const labels = { operatorName, workerTypeName, job };
      if (existingAllocationId) {
        const existing = workerAllocations.find(wa => wa.id === existingAllocationId);
        if (!existing) {
          toast.error('Could not find existing allocation');
          return;
        }
        return isAllDays
          ? swapCrewBlockAllDays(jobId, existing, resourceId, labels)
          : swapCrewBlockDay(jobId, existing, resourceId, labels);
      }
      if (!isAllDays) return fillCrewBlockDay(jobId, workerTypeId, resourceId, labels);
    }

    // Un-confirm the job if it was confirmed
    if (job.status === 'confirmed') {
      await unconfirmJobIfNeeded(jobId, 'allocated');
    }

    if (existingAllocationId) {
      // Slot already has an operator - UPDATE the existing allocation
      const existingAllocation = workerAllocations.find(wa => wa.id === existingAllocationId);
      if (!existingAllocation) {
        toast.error('Could not find existing allocation');
        return;
      }
      
      const previousOperatorId = existingAllocation.operator_id;
      
      // Optimistic update
      setWorkerAllocations(prev => 
        prev.map(wa => wa.id === existingAllocationId 
          ? { ...wa, operator_id: resourceId, ...(isAllDays ? { start_date: job.start_date, end_date: job.end_date } : {}) } 
          : wa
        )
      );

      try {
        const patchData = { operator_id: resourceId };
        if (isAllDays) {
          patchData.start_date = job.start_date;
          patchData.end_date = job.end_date;
        }
        await api.patch(`/worker-allocations/${existingAllocationId}`, patchData);
        toast.success(`${operatorName} replaced previous operator as ${workerTypeName}${isAllDays ? ' for all days' : ''}`);
        await syncJobStatus(jobId);
      } catch (error) {
        const lb = getLeaveBlock(error);
        if (lb) {
          const patchForRetry = { operator_id: resourceId };
          if (isAllDays) {
            patchForRetry.start_date = job.start_date;
            patchForRetry.end_date = job.end_date;
          }
          setLeaveOverride({
            open: true,
            blocks: [lb],
            callback: async (reason) => {
              try {
                await api.patch(withLeaveOverride(`/worker-allocations/${existingAllocationId}`, reason), patchForRetry);
                toast.success(`${operatorName} replaced previous operator as ${workerTypeName}${isAllDays ? ' for all days' : ''}`);
                await syncJobStatus(jobId);
              } catch (e) {
                setWorkerAllocations(prev =>
                  prev.map(wa => wa.id === existingAllocationId
                    ? { ...wa, operator_id: previousOperatorId }
                    : wa
                  )
                );
                toast.error(getApiErrorMessage(e, 'Failed to update worker assignment'));
              }
            },
            onCancel: () => {
              setWorkerAllocations(prev =>
                prev.map(wa => wa.id === existingAllocationId
                  ? { ...wa, operator_id: previousOperatorId }
                  : wa
                )
              );
            },
          });
          return;
        }
        setWorkerAllocations(prev => 
          prev.map(wa => wa.id === existingAllocationId 
            ? { ...wa, operator_id: previousOperatorId } 
            : wa
          )
        );
        toast.error(getApiErrorMessage(error, 'Failed to update worker assignment'));
      }
    } else {
      // Empty slot - CREATE new allocation
      const allocStartDate = isAllDays ? job.start_date : dateStr;
      const allocEndDate = isAllDays ? job.end_date : (job.end_date && job.end_date !== job.start_date ? dateStr : null);
      const tempId = `temp-${Date.now()}`;
      const newWorkerAllocation = {
        id: tempId,
        job_id: jobId,
        worker_type_id: workerTypeId,
        operator_id: resourceId,
        start_date: allocStartDate,
        end_date: allocEndDate,
      };
      
      setWorkerAllocations(prev => [...prev, newWorkerAllocation]);
      toast.success(`${operatorName} assigned as ${workerTypeName}${isAllDays ? ' for all days' : ''}`);

      try {
        const response = await api.post('/worker-allocations', {
          job_id: jobId,
          worker_type_id: workerTypeId,
          operator_id: resourceId,
          start_date: allocStartDate,
          end_date: allocEndDate,
        });
        
        setWorkerAllocations(prev => 
          prev.map(wa => wa.id === tempId ? response.data : wa)
        );
        if ((response.data?.pieces?.length || 0) > 1) await refreshJobCrew(jobId);
        await syncJobStatus(jobId);
      } catch (error) {
        const lb = getLeaveBlock(error);
        if (lb) {
          setWorkerAllocations(prev => prev.filter(wa => wa.id !== tempId));
          setLeaveOverride({
            open: true,
            blocks: [lb],
            callback: async (reason) => {
              try {
                const resp = await api.post(withLeaveOverride('/worker-allocations', reason), {
                  job_id: jobId, worker_type_id: workerTypeId, operator_id: resourceId,
                  start_date: allocStartDate, end_date: allocEndDate,
                });
                setWorkerAllocations(prev => [...prev, resp.data]);
                if ((resp.data?.pieces?.length || 0) > 1) await refreshJobCrew(jobId);
                await syncJobStatus(jobId);
              } catch (e) {
                toast.error(getApiErrorMessage(e, 'Failed to assign worker'));
              }
            },
          });
          return;
        }
        setWorkerAllocations(prev => prev.filter(wa => wa.id !== tempId));
        toast.error(getApiErrorMessage(error, 'Failed to assign worker'));
      }
    }
  };

  // Handle drag end - create or update allocation with optimistic updates
  // ---- Trailer Load (P2) helpers ----
  const handleAddLoad = async (allocationId, itemType, itemId) => {
    const allocation = allocations.find(a => a.id === allocationId);
    if (!allocation) return;
    const field = itemType === 'attachment' ? 'cargo_attachment_ids' : 'cargo_equipment_ids';
    const current = allocation[field] || [];
    if (current.includes(itemId)) return; // idempotent

    // Optimistic update
    setAllocations(prev => prev.map(a =>
      a.id === allocationId ? { ...a, [field]: [...current, itemId] } : a
    ));
    try {
      const res = await api.post(`/allocations/${allocationId}/cargo`, {
        item_type: itemType,
        item_id: itemId,
      });
      if (res.data?.allocation) {
        setAllocations(prev => prev.map(a => a.id === allocationId ? res.data.allocation : a));
      }
      const label = itemType === 'attachment'
        ? (attachments.find(x => x.id === itemId)?.name || 'Attachment')
        : (equipment.find(x => x.id === itemId)?.asset_id || 'Asset');
      toast.success(`Added ${label} to load`);
    } catch (e) {
      // Rollback on failure
      setAllocations(prev => prev.map(a =>
        a.id === allocationId ? { ...a, [field]: current } : a
      ));
      const msg = e?.response?.data?.detail;
      const text = typeof msg === 'string' ? msg : msg?.message || 'Could not add to load';
      toast.error(text);
    }
  };

  const handleRemoveLoad = async (allocationId, itemType, itemId) => {
    const allocation = allocations.find(a => a.id === allocationId);
    if (!allocation) return;
    const field = itemType === 'attachment' ? 'cargo_attachment_ids' : 'cargo_equipment_ids';
    const current = allocation[field] || [];
    // Optimistic
    setAllocations(prev => prev.map(a =>
      a.id === allocationId ? { ...a, [field]: current.filter(x => x !== itemId) } : a
    ));
    try {
      await api.delete(`/allocations/${allocationId}/cargo/${itemId}?item_type=${itemType}`);
    } catch {
      // Rollback
      setAllocations(prev => prev.map(a =>
        a.id === allocationId ? { ...a, [field]: current } : a
      ));
      toast.error('Could not remove from load');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveData(null);
    setIsDragging(false);

    if (!over) return;

    // Parse the active ID correctly - format is "type-uuid" where uuid contains hyphens
    const activeIdStr = active.id;
    const firstDash = activeIdStr.indexOf('-');
    const resourceType = activeIdStr.substring(0, firstDash);
    const resourceId = activeIdStr.substring(firstDash + 1);
    
    const overIdStr = over.id;
    const dateStr = format(currentDate, 'yyyy-MM-dd');

    // Trailer Load (P2) — drop asset or attachment onto a Load strip
    if (overIdStr.startsWith('load-')) {
      const allocationId = overIdStr.replace('load-', '');
      if (resourceType === 'equipment' || resourceType === 'attachment') {
        await handleAddLoad(allocationId, resourceType === 'attachment' ? 'attachment' : 'equipment', resourceId);
      }
      return;
    }

    // Check if dropping on a worker slot (uses __ as separator)
    if (overIdStr.startsWith('worker__') && resourceType === 'operator') {
      const dropData = over.data?.current || {};
      const { jobId, workerTypeId, allocationId } = dropData;
      
      const job = jobs.find(j => j.id === jobId);
      if (!job) return;

      // --- Worker type validation: block workers without the required type ---
      if (!tenantSettings.relaxed_allocation && workerTypeId) {
        const operator = operators.find(o => o.id === resourceId);
        const opWorkerTypes = operator?.worker_type_ids || [];
        if (!opWorkerTypes.includes(workerTypeId)) {
          const workerName = operator?.name || 'Worker';
          const wtName = getWorkerTypeName(workerTypeId);
          toast.error(`Worker ${workerName} does not match Worker Type ${wtName}.`);
          return;
        }
      }

      // Multi-day job on Day 1? Prompt user for scope
      const isMultiDay = job.end_date && job.end_date !== job.start_date;
      if (isMultiDay && dateStr === job.start_date) {
        const workerTypeName = getWorkerTypeName(workerTypeId);
        const operatorName = operators.find(o => o.id === resourceId)?.name || 'Operator';
        setMultiDayPrompt({
          open: true, jobId, job,
          action: 'allocate_worker',
          value: { resourceId, workerTypeId, allocationId, operatorName, workerTypeName },
        });
        return;
      }

      // Single-day or not Day 1 → allocate for this day only
      await applyWorkerSlotAllocation(jobId, job, resourceId, workerTypeId, allocationId, 'day');
      return;
    }

    // Standard job allocation (equipment or operator)
    if (!overIdStr.startsWith('job-')) return;
    
    const jobId = overIdStr.replace('job-', '');
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;

    // --- Asset type validation: block mismatched equipment on drop ---
    if (resourceType === 'equipment' && job.equipment_type) {
      const draggedAsset = equipment.find(e => e.id === resourceId);
      if (draggedAsset) {
        const jobEqType = equipmentTypes.find(t => t.name === job.equipment_type);
        const isPrimeMoverType = jobEqType?.requires_prime_mover && jobEqType?.prime_mover_asset_type_id &&
          draggedAsset.equipment_type === equipmentTypes.find(t => t.id === jobEqType.prime_mover_asset_type_id)?.name;
        if (!tenantSettings.relaxed_allocation && !isPrimeMoverType && draggedAsset.equipment_type !== job.equipment_type) {
          toast.error(`Asset does not match Asset Type required. Job requires "${job.equipment_type}" but "${draggedAsset.asset_id || draggedAsset.name}" is "${draggedAsset.equipment_type}".`);
          return;
        }
      }
    }

    // --- Primary operator validation: block workers who don't operate the required asset type ---
    if (!tenantSettings.relaxed_allocation && resourceType === 'operator' && job.equipment_type) {
      const operator = operators.find(o => o.id === resourceId);
      if (operator) {
        const opEqTypes = operator.equipment_types || [];
        if (opEqTypes.length === 0) {
          toast.error(`Worker ${operator.name} is not set to operate any asset types. Job requires "${job.equipment_type}".`);
          return;
        }
        if (!opEqTypes.includes(job.equipment_type)) {
          toast.error(`Worker ${operator.name} does not operate "${job.equipment_type}". They operate: ${opEqTypes.join(', ')}.`);
          return;
        }
      }
    }

    // Multi-day job on Day 1? Prompt user for scope
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    if (isMultiDay && dateStr === job.start_date) {
      const label = resourceType === 'equipment'
        ? (equipment.find(e => e.id === resourceId)?.asset_id || 'Asset')
        : (operators.find(o => o.id === resourceId)?.name || 'Operator');
      setMultiDayPrompt({
        open: true, jobId, job,
        action: 'allocate',
        value: { resourceType, resourceId, label },
      });
      return;
    }

    // Single-day or not Day 1 → allocate for this day only
    await applyStandardAllocation(jobId, job, resourceType, resourceId, 'day');
  };

  // Remove allocation (asset or operator) with optimistic update
  const handleRemoveFromAllocation = async (allocationId, field) => {
    const allocation = allocations.find(a => a.id === allocationId);
    if (!allocation) return;

    // Find the job to check/update its status
    const job = jobs.find(j => j.id === allocation.job_id);

    // Block removal on docketed/invoiced jobs
    if (job && (job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled')) {
      toast.error('Cannot modify allocation for a docketed, invoiced or cancelled job');
      return;
    }

    // Multi-day block job: split this day off first so the removal only affects this day
    if (job && isBlockJob(job)) {
      try {
        await isolateDayBlock(allocation, format(currentDate, 'yyyy-MM-dd'));
      } catch { return; }
    }

    // Prime mover is auxiliary — removing it never deletes the allocation
    const isPrimeMoverField = field === 'prime_mover_equipment_id';

    // Check if both fields would be empty after removal (skip for aux fields)
    const otherField = field === 'equipment_id' ? 'operator_id' : 'equipment_id';
    const otherValue = isPrimeMoverField ? 'keep' : allocation[otherField];
    
    // Store original for rollback
    const originalAllocations = [...allocations];
    
    try {
      if (!otherValue) {
        // Removing last item - allocation will be deleted
        setAllocations(prev => prev.filter(a => a.id !== allocationId));
        toast.success('Allocation removed');
        await api.delete(`/allocations/${allocationId}`);
        
        // Optimistic guess, then reconcile with backend truth
        if (job) {
          const hasWorkers = workerAllocations.some(wa => wa.job_id === allocation.job_id);
          const newStatus = hasWorkers ? 'partially_allocated' : 'not_allocated';
          setJobs(prev => prev.map(j => 
            j.id === allocation.job_id ? { ...j, status: newStatus } : j
          ));
        }
        await syncJobStatus(allocation.job_id);
      } else {
        // Still has other resource - clear the removed field
        setAllocations(prev => prev.map(a => 
          a.id === allocationId ? { ...a, [field]: '' } : a
        ));
        toast.success(`${field === 'equipment_id' ? 'Asset' : field === 'prime_mover_equipment_id' ? 'Prime mover' : 'Operator'} removed`);
        await api.patch(`/allocations/${allocationId}`, { [field]: null });
        
        // Update job status - partially_allocated since only one resource remains (skip for aux fields)
        if (job && !isPrimeMoverField) {
          const newStatus = 'partially_allocated';
          setJobs(prev => prev.map(j => 
            j.id === allocation.job_id ? { ...j, status: newStatus } : j
          ));
        }
        await syncJobStatus(allocation.job_id);
      }
    } catch (error) {
      // Rollback on error
      setAllocations(originalAllocations);
      toast.error('Failed to remove');
    }
  };

  // Remove a specific trailer from a multi-trailer rig allocation
  const handleRemoveTrailer = async (allocationId, trailerId) => {
    const allocation = allocations.find(a => a.id === allocationId);
    if (!allocation) return;
    const job = jobs.find(j => j.id === allocation.job_id);
    if (job && (job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled')) {
      toast.error('Cannot modify allocation for a docketed, invoiced or cancelled job');
      return;
    }
    if (job && isBlockJob(job)) {
      try {
        await isolateDayBlock(allocation, format(currentDate, 'yyyy-MM-dd'));
      } catch { return; }
    }
    const next = (allocation.additional_trailer_equipment_ids || []).filter(t => t && t !== trailerId);
    const original = [...allocations];
    try {
      setAllocations(prev => prev.map(a => a.id === allocationId ? { ...a, additional_trailer_equipment_ids: next } : a));
      toast.success('Trailer removed');
      await api.patch(`/allocations/${allocationId}?skip_validation=true`, { additional_trailer_equipment_ids: next });
    } catch (error) {
      setAllocations(original);
      toast.error('Failed to remove trailer');
    }
  };

  // Remove worker allocation with optimistic update
  const handleRemoveWorkerAllocation = async (workerAllocationId) => {
    // Find the worker allocation to get job_id
    const workerAllocation = workerAllocations.find(wa => wa.id === workerAllocationId);
    if (!workerAllocation) return;
    
    const job = jobs.find(j => j.id === workerAllocation.job_id);

    // Block removal on docketed/invoiced jobs
    if (job && (job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled')) {
      toast.error('Cannot modify allocation for a docketed, invoiced or cancelled job');
      return;
    }

    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const jobEndDate = job?.end_date || job?.start_date || dateStr;
    const waEffectiveEnd = workerAllocation.end_date || jobEndDate;
    const waStart = workerAllocation.start_date;

    // UTC-safe date arithmetic
    const addDays = (ds, days) => {
      const [y, m, d] = ds.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d + days)).toISOString().split('T')[0];
    };

    // Multi-day block job, linked row: split this day off its block; the row then covers only this day
    let isolatedLinked = false;
    if (job && isBlockJob(job) && workerAllocation.allocation_id) {
      const block = allocations.find(a => a.id === workerAllocation.allocation_id);
      if (block) {
        try {
          await isolateDayBlock(block, dateStr);
          isolatedLinked = true;
        } catch { return; }
      }
    }

    // Check if this WA spans more than just the current day
    const spansMultipleDays = !isolatedLinked && (waStart < dateStr || waEffectiveEnd > dateStr);
    
    // Store original for rollback
    const originalWorkerAllocations = [...workerAllocations];
    
    // Optimistic update — remove from current day view
    setWorkerAllocations(prev => prev.filter(wa => wa.id !== workerAllocationId));
    toast.success('Worker removed');
    
    try {
      if (spansMultipleDays) {
        // Create remainder(s) before deleting the original
        // Remainder BEFORE current date (e.g., WA 13-15, removing on 15 → keep 13-14)
        if (waStart < dateStr) {
          const prevDay = addDays(dateStr, -1);
          if (prevDay >= waStart) {
            const res = await api.post('/worker-allocations', {
              job_id: workerAllocation.job_id,
              worker_type_id: workerAllocation.worker_type_id,
              operator_id: workerAllocation.operator_id,
              start_date: waStart,
              end_date: prevDay,
              ...(workerAllocation.allocation_id ? { allocation_id: workerAllocation.allocation_id } : {}),
            });
            // Add the remainder to local state
            setWorkerAllocations(prev => [...prev, res.data]);
          }
        }
        // Remainder AFTER current date (e.g., WA 13-17, removing on 15 → keep 16-17)
        if (waEffectiveEnd > dateStr) {
          const nextDay = addDays(dateStr, 1);
          if (nextDay <= waEffectiveEnd) {
            const res = await api.post('/worker-allocations', {
              job_id: workerAllocation.job_id,
              worker_type_id: workerAllocation.worker_type_id,
              operator_id: workerAllocation.operator_id,
              start_date: nextDay,
              end_date: waEffectiveEnd,
              ...(workerAllocation.allocation_id ? { allocation_id: workerAllocation.allocation_id } : {}),
            });
            setWorkerAllocations(prev => [...prev, res.data]);
          }
        }
      }

      // Delete the original
      await api.delete(`/worker-allocations/${workerAllocationId}`);
      
      // Un-confirm the job if it was confirmed (removing worker = allocation change)
      if (job && job.status === 'confirmed') {
        await unconfirmJobIfNeeded(workerAllocation.job_id, 'allocated');
      }
      await syncJobStatus(workerAllocation.job_id);
    } catch (error) {
      // Rollback on error
      setWorkerAllocations(originalWorkerAllocations);
      toast.error('Failed to remove worker');
    }
  };

  // Handle job move (change start time)
  const handleJobMove = async (jobId, newStartTime) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    
    // Check if job is docketed - don't allow changes
    if (job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled') {
      toast.error('Cannot modify a docketed, invoiced or cancelled job');
      return;
    }
    
    // Check if multi-day job — prompt user
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    if (isMultiDay) {
      setMultiDayPrompt({ open: true, jobId, job, action: 'move', value: newStartTime });
      return;
    }
    
    await applyJobMove(jobId, job, newStartTime, 'all');
  };

  const applyJobMove = async (jobId, job, newStartTime, scope) => {
    if (scope === 'day') {
      // Save per-day override via job_day_details
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      try {
        await api.post('/job-day-details', {
          job_id: jobId,
          date: dateStr,
          start_time: newStartTime,
        });
        // Update local display
        setJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, _dayStartTime: newStartTime } : j
        ));
        toast.success(`Start time for ${dateStr} changed to ${newStartTime}`);
      } catch (error) {
        toast.error('Failed to update day-specific time');
      }
      return;
    }

    // "All days" — original logic
    const hasDepotTime = tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== job.start_time;
    let newDepotTime = null;
    let actualNewStartTime = newStartTime;
    
    if (hasDepotTime) {
      newDepotTime = newStartTime;
      const depotMinutes = parseInt(job.depot_time.split(':')[0]) * 60 + parseInt(job.depot_time.split(':')[1]);
      const startMinutes = parseInt(job.start_time.split(':')[0]) * 60 + parseInt(job.start_time.split(':')[1]);
      const offset = startMinutes - depotMinutes;
      const newDepotMinutes = parseInt(newStartTime.split(':')[0]) * 60 + parseInt(newStartTime.split(':')[1]);
      const newStartMinutes = newDepotMinutes + offset;
      const h = Math.floor(newStartMinutes / 60) % 24;
      const m = newStartMinutes % 60;
      actualNewStartTime = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    
    // Store original for rollback
    const originalJobs = [...jobs];
    const wasConfirmed = job.status === 'confirmed';
    
    // Optimistic update
    setJobs(prev => prev.map(j => 
      j.id === jobId 
        ? { ...j, start_time: actualNewStartTime, ...(newDepotTime ? { depot_time: newDepotTime } : {}), status: wasConfirmed ? 'allocated' : j.status }
        : j
    ));
    
    if (wasConfirmed) {
      toast.info('Job un-confirmed due to schedule change');
    } else {
      toast.success(`Start time changed to ${actualNewStartTime}${newDepotTime ? ` (depot: ${newDepotTime})` : ''}`);
    }
    
    try {
      // Send full job object with updates (required by JobUpdate model) - use PUT
      const updateData = {
        customer_id: job.customer_id,
        site_id: job.site_id || '',
        one_off_street: job.one_off_street,
        one_off_suburb: job.one_off_suburb,
        one_off_state: job.one_off_state,
        one_off_postcode: job.one_off_postcode,
        equipment_type: job.equipment_type,
        attachment_type_ids: job.attachment_type_ids || [],
        worker_requirements: job.worker_requirements || [],
        start_date: job.start_date,
        start_time: actualNewStartTime,
        depot_time: newDepotTime || job.depot_time || null,
        end_date: job.end_date,
        est_duration: job.est_duration,
        description: job.description || '',
        status: wasConfirmed ? 'allocated' : job.status,
        notes: job.notes,
        tags: job.tags || [],
        po_number: job.po_number,
        version: job.version,
      };
      
      const response = await api.put(`/jobs/${jobId}`, updateData);
      // Update local job with new version (don't full refresh - keeps stable row order)
      if (response.data?.version !== undefined) {
        setJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, start_time: actualNewStartTime, ...(newDepotTime ? { depot_time: newDepotTime } : {}), version: response.data.version } : j
        ));
      }
    } catch (error) {
      // Rollback on error
      setJobs(originalJobs);
      const detail = error.response?.data?.detail;
      // Handle object error responses (e.g., version conflict)
      if (typeof detail === 'object' && detail.error === 'version_conflict') {
        toast.error('Job was modified elsewhere. Refreshing data...');
        fetchData(); // Refresh to get latest version
      } else {
        const errorMsg = typeof detail === 'object' ? detail.message || 'Failed to update job time' : detail || 'Failed to update job time';
        toast.error(errorMsg);
      }
    }
  };

  // Handle job resize (change duration)
  const handleJobResize = async (jobId, newDuration) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    
    // Check if job is docketed - don't allow changes
    if (job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled') {
      toast.error('Cannot modify a docketed, invoiced or cancelled job');
      return;
    }
    
    // Check if multi-day job — prompt user
    const isMultiDay = job.end_date && job.end_date !== job.start_date;
    if (isMultiDay) {
      setMultiDayPrompt({ open: true, jobId, job, action: 'resize', value: newDuration });
      return;
    }
    
    await applyJobResize(jobId, job, newDuration, 'all');
  };

  const applyJobResize = async (jobId, job, newDuration, scope) => {
    if (scope === 'day') {
      // Save per-day override via job_day_details
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      try {
        await api.post('/job-day-details', {
          job_id: jobId,
          date: dateStr,
          est_duration: newDuration,
        });
        setJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, _dayDuration: newDuration } : j
        ));
        toast.success(`Duration for ${dateStr} changed to ${newDuration}h`);
      } catch (error) {
        toast.error('Failed to update day-specific duration');
      }
      return;
    }

    // "All days" — original logic
    const originalJobs = [...jobs];
    const wasConfirmed = job.status === 'confirmed';
    
    // Optimistic update
    setJobs(prev => prev.map(j => 
      j.id === jobId 
        ? { ...j, est_duration: newDuration, status: wasConfirmed ? 'allocated' : j.status }
        : j
    ));
    
    if (wasConfirmed) {
      toast.info('Job un-confirmed due to duration change');
    } else {
      toast.success(`Duration changed to ${newDuration}h`);
    }
    
    try {
      // Send full job object with updates (required by JobUpdate model) - use PUT
      const updateData = {
        customer_id: job.customer_id,
        site_id: job.site_id || '',
        one_off_street: job.one_off_street,
        one_off_suburb: job.one_off_suburb,
        one_off_state: job.one_off_state,
        one_off_postcode: job.one_off_postcode,
        equipment_type: job.equipment_type,
        attachment_type_ids: job.attachment_type_ids || [],
        worker_requirements: job.worker_requirements || [],
        start_date: job.start_date,
        start_time: job.start_time,
        end_date: job.end_date,
        est_duration: newDuration,
        description: job.description || '',
        status: wasConfirmed ? 'allocated' : job.status,
        notes: job.notes,
        tags: job.tags || [],
        po_number: job.po_number,
        version: job.version,
      };
      
      const response = await api.put(`/jobs/${jobId}`, updateData);
      // Update local job with new version (don't full refresh - keeps stable row order)
      if (response.data?.version !== undefined) {
        setJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, est_duration: newDuration, version: response.data.version } : j
        ));
      }
    } catch (error) {
      // Rollback on error
      setJobs(originalJobs);
      const detail = error.response?.data?.detail;
      // Handle object error responses (e.g., version conflict)
      if (typeof detail === 'object' && detail.error === 'version_conflict') {
        toast.error('Job was modified elsewhere. Refreshing data...');
        fetchData(); // Refresh to get latest version
      } else {
        const errorMsg = typeof detail === 'object' ? detail.message || 'Failed to update job duration' : detail || 'Failed to update job duration';
        toast.error(errorMsg);
      }
    }
  };

  // Handle multi-day prompt confirmation
  const handleMultiDayConfirm = async (scope) => {
    const { jobId, job, action, value } = multiDayPrompt;
    setMultiDayPrompt({ open: false, jobId: null, job: null, action: null, value: null });
    
    if (action === 'move') {
      await applyJobMove(jobId, job, value, scope);
    } else if (action === 'resize') {
      await applyJobResize(jobId, job, value, scope);
    } else if (action === 'allocate') {
      await applyStandardAllocation(jobId, job, value.resourceType, value.resourceId, scope);
    } else if (action === 'allocate_worker') {
      await applyWorkerSlotAllocation(jobId, job, value.resourceId, value.workerTypeId, value.allocationId, scope);
    }
  };

  // Get status color  
  const getStatusColor = (status) => {
    const colors = {
      'not_allocated': { bg: 'bg-zinc-300/50 dark:bg-zinc-700', border: 'border-zinc-400 dark:border-zinc-500', text: 'text-zinc-600 dark:text-zinc-300' },
      'partially_allocated': { bg: 'bg-orange-500/30', border: 'border-orange-500', text: 'text-orange-600 dark:text-orange-400' },
      'allocated': { bg: 'bg-amber-500/30', border: 'border-amber-500', text: 'text-amber-600 dark:text-amber-400' },
      'mixed': { bg: 'bg-orange-400/30', border: 'border-orange-400', text: 'text-orange-500 dark:text-orange-300' },
      'confirmed': { bg: 'bg-blue-500/30', border: 'border-blue-500', text: 'text-blue-600 dark:text-blue-400' },
      'docketed': { bg: 'bg-purple-500/30', border: 'border-purple-500', text: 'text-purple-600 dark:text-purple-400' },
      'invoiced': { bg: 'bg-green-500/30', border: 'border-green-500', text: 'text-green-600 dark:text-green-400' },
      'tbc': { bg: 'bg-zinc-500/30', border: 'border-zinc-400', text: 'text-zinc-500 dark:text-zinc-400' },
      'cancelled': { bg: 'bg-zinc-400/20 dark:bg-zinc-800/60', border: 'border-zinc-400 dark:border-zinc-600', text: 'text-zinc-500 line-through' },
    };
    return colors[status] || colors['not_allocated'];
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate timeline width
  const timelineWidth = TIME_SLOTS.length * SLOT_WIDTH;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">Loading planning board...</p>
        </div>
      </div>
    );
  }

  const renderUnassignedSection = () => (
    <div>
      <div className="flex border-b border-amber-600/40 cursor-pointer hover:bg-muted/30 transition-colors" style={{ height: 32 }}>
        <div className="w-[140px] flex-shrink-0 border-r bg-amber-900/20 sticky left-0 z-10 flex items-center px-2 gap-1.5">
          <span className="text-[11px] font-semibold text-amber-400">Needs Allocation</span>
        </div>
        <div className="w-[180px] flex-shrink-0 border-r bg-amber-900/20 sticky left-[140px] z-10 flex items-center px-2">
          <span className="text-[10px] text-amber-400/70">{assetGroupedJobs.unassigned.length} job{assetGroupedJobs.unassigned.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex-1 bg-amber-900/10" />
      </div>
      {assetGroupedJobs.unassigned.map((job) => {
        const allocation = getJobAllocation(job.id);
        const customer = getCustomerInfo(job.customer_id);
        const assignedOperator = allocation?.operator_id ? getOperatorInfo(allocation.operator_id) : null;
        const jobWorkerAllocations = getJobWorkerAllocations(job.id);
        const statusColor = getStatusColor(job.is_tbc ? 'tbc' : 'not_allocated');
        const dayStatus = job.is_tbc ? 'tbc' : 'not_allocated';
        const pbJobEqType = equipmentTypes.find(t => t.name === job.equipment_type);
        const pbIsPrimeMoverJob = !!pbJobEqType?.requires_prime_mover;
        const pbPrimeMoverTypeName = pbIsPrimeMoverJob
          ? (equipmentTypes.find(t => t.id === pbJobEqType.prime_mover_asset_type_id)?.name || 'Prime Mover')
          : null;
        const pbAssignedPrimeMover = allocation?.prime_mover_equipment_id
          ? getEquipmentInfo(allocation.prime_mover_equipment_id)
          : null;
        const pbTrailerCount = pbIsPrimeMoverJob ? (pbJobEqType?.trailer_count || 1) : 1;
        const pbExtraTrailerSlots = pbTrailerCount > 1 ? Array.from({ length: pbTrailerCount - 1 }).map((_, i) => {
          const tid = (allocation?.additional_trailer_equipment_ids || [])[i];
          return { index: i, trailerId: tid, equipment: tid ? getEquipmentInfo(tid) : null };
        }) : [];
        const workerSlots = [];
        (job.worker_requirements || []).forEach((wr) => {
          const wtInfo = getWorkerTypeInfo(wr.worker_type_id);
          for (let i = 0; i < (wr.quantity || 1); i++) {
            const existingAllocation = jobWorkerAllocations.find(wa => wa.worker_type_id === wr.worker_type_id && jobWorkerAllocations.filter(x => x.worker_type_id === wr.worker_type_id).indexOf(wa) === i);
            workerSlots.push({ worker_type_id: wr.worker_type_id, worker_type_name: wtInfo.name, worker_type_color: wtInfo.color, index: i, allocation: existingAllocation });
          }
        });
        const effectiveJobStartTime = job._dayStartTime || job.start_time || '07:00';
        const effectiveJobDuration = job._dayDuration || job.est_duration || 2;
        const hasDepotTime = tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== effectiveJobStartTime;
        const effectiveStartTime = hasDepotTime ? job.depot_time : effectiveJobStartTime;
        const leftOffset = timeToPixel(effectiveStartTime);
        let barWidth;
        if (hasDepotTime && effectiveJobDuration) {
          barWidth = timeToPixel(effectiveJobStartTime) + durationToPixel(effectiveJobDuration) - leftOffset;
        } else {
          barWidth = durationToPixel(effectiveJobDuration);
        }
        const hasWorkers = workerSlots.length > 0;
        const dynamicHeight = hasWorkers ? Math.max(ROW_HEIGHT, 80 + workerSlots.length * 22) : ROW_HEIGHT;
        return (
          <div key={job.id} className="flex border-b border-zinc-800/50" style={{ minHeight: dynamicHeight }}>
            <div className="w-[140px] flex-shrink-0 border-r bg-amber-900/10 sticky left-0 z-10" />
            <div className="w-[180px] flex-shrink-0 border-r p-2 bg-muted sticky left-[140px] z-10 relative group/pbcard">
              <button data-testid={`pb-delete-job-${job.id}`} className="absolute top-1 right-1 p-0.5 rounded opacity-0 group-hover/pbcard:opacity-100 transition-opacity bg-red-500/80 hover:bg-red-600 text-white z-20" onClick={(e) => { e.stopPropagation(); setDeleteJobDialog({ open: true, job }); }} title="Delete job"><Trash2 className="h-3 w-3" /></button>
              <div className="text-xs font-medium truncate">{customer?.company_name || 'Unknown'}</div>
              <div className="text-[10px] text-muted-foreground truncate">{job.equipment_type}</div>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                <Badge variant="outline" className={`text-[9px] ${statusColor.text} border-current`}>{dayStatus.replace('_', ' ')}</Badge>
              </div>
            </div>
            <div className="flex-1 relative">
              <div className="absolute inset-0 flex pointer-events-none">
                {TIME_SLOTS.map((slot) => (<div key={`slot-${slot.hour}-${slot.minute}`} className={`flex-shrink-0 border-r ${slot.minute === 0 ? 'border-zinc-600' : 'border-zinc-800'}`} style={{ width: SLOT_WIDTH }} />))}
              </div>
              <ResizableGanttBar job={job} leftOffset={leftOffset} barWidth={Math.max(barWidth, 180)} minHeight={dynamicHeight - 16} statusColor={statusColor} isLocked={job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled'} onMove={handleJobMove} onResize={handleJobResize} onBarClick={handleBarClick} setupGroupTheme={null}>
                <DroppableGanttBar jobId={job.id}>
                  <div className="flex items-center gap-1 text-[10px]">
                    <span className="font-mono font-bold">{formatTime(effectiveJobStartTime, tenantSettings.time_format)}{job.end_time && (<span className="font-normal text-muted-foreground/80 ml-0.5"> – {formatTime(job.end_time, tenantSettings.time_format)}</span>)}</span>
                    <span className="text-muted-foreground">{effectiveJobDuration ? `(${effectiveJobDuration}h)` : ''}</span>
                    <Badge variant="outline" className="text-[8px] px-1 py-0 ml-auto" data-testid={`pb-job-num-un-${job.id}`}>
                      {job.job_number}
                      {(() => {
                        if (!job.setup_group_id || job.is_setup_parent) return null;
                        const parent = jobs.find(j => j.is_setup_parent && j.setup_group_id === job.setup_group_id);
                        return parent ? <span className="text-muted-foreground ml-1 font-normal">· #{parent.job_number}</span> : null;
                      })()}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap mt-1">
                    <div className={`text-[9px] text-zinc-500 italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'equipment' ? 'border-blue-400 bg-blue-500/20 text-blue-400 animate-pulse' : 'border-zinc-600'}`}>+ Asset</div>
                    {pbExtraTrailerSlots.map((slot) => (
                      slot.equipment
                        ? <div key={slot.index} data-testid={`pb-trailer-assigned-${job.id}-${slot.index + 2}`}><AllocationBubble type="equipment" label={slot.equipment.asset_id || slot.equipment.name} onRemove={() => handleRemoveTrailer(allocation.id, slot.trailerId)} /></div>
                        : <div key={slot.index} className={`text-[9px] italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'equipment' ? 'border-amber-400 bg-amber-500/20 text-amber-400 animate-pulse' : 'border-amber-500/50 text-amber-400/80'}`} data-testid={`pb-trailer-empty-${job.id}-${slot.index + 2}`}>+ Trailer {slot.index + 2}</div>
                    ))}
                    {pbIsPrimeMoverJob && (
                      pbAssignedPrimeMover
                        ? <div data-testid={`pb-prime-mover-assigned-${job.id}`}><AllocationBubble type="equipment" label={pbAssignedPrimeMover.asset_id || pbAssignedPrimeMover.name} onRemove={() => handleRemoveFromAllocation(allocation.id, 'prime_mover_equipment_id')} /></div>
                        : <div className={`text-[9px] italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'equipment' ? 'border-blue-400 bg-blue-500/20 text-blue-400 animate-pulse' : 'border-blue-500/50 text-blue-400/80'}`} data-testid={`pb-prime-mover-empty-${job.id}`}>+ {pbPrimeMoverTypeName}</div>
                    )}
                    {assignedOperator ? (<AllocationBubble type="operator" label={assignedOperator.name} onRemove={() => handleRemoveFromAllocation(allocation.id, 'operator_id')} />) : (<div className={`text-[9px] text-zinc-500 italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'operator' ? 'border-green-400 bg-green-500/20 text-green-400 animate-pulse' : 'border-zinc-600'}`}>+ Operator</div>)}
                  </div>
                  {workerSlots.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-zinc-600/50 flex flex-wrap gap-1">
                      {workerSlots.map((slot, idx) => (
                        <DroppableWorkerSlot key={`${slot.worker_type_id}-${slot.index}`} jobId={job.id} workerTypeId={slot.worker_type_id} workerTypeName={slot.worker_type_name} index={slot.index} isAssigned={!!slot.allocation} allocationId={slot.allocation?.id}>
                          {slot.allocation ? (
                            <AllocationBubble type="worker" label={(() => { const op = getOperatorInfo(slot.allocation.operator_id); return op ? op.name : 'Unknown'; })()} color={slot.worker_type_color} workerTypeName={slot.worker_type_name} onRemove={() => handleRemoveWorkerAllocation(slot.allocation.id)} />
                          ) : (
                            <div className={`text-[9px] italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'operator' ? 'border-purple-400 bg-purple-500/20 text-purple-400 animate-pulse' : 'border-zinc-600 text-zinc-500'}`} style={slot.worker_type_color ? { borderColor: `${slot.worker_type_color}40`, color: slot.worker_type_color } : {}}>+ {slot.worker_type_name}</div>
                          )}
                        </DroppableWorkerSlot>
                      ))}
                    </div>
                  )}
                  {(() => {
                    if (!allocation) return null;
                    const loadEqIds = allocation.cargo_equipment_ids || [];
                    const loadAttIds = allocation.cargo_attachment_ids || [];
                    const hasItems = loadEqIds.length + loadAttIds.length > 0;
                    if (!hasItems && !pbIsPrimeMoverJob) return null;
                    const total = loadEqIds.length + loadAttIds.length;
                    return (
                      <div className="mt-1 pt-1 border-t border-zinc-600/50">
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-[9px] text-amber-300/80 font-semibold tracking-wide uppercase">Load{total > 0 ? ` (${total})` : ''}</span>
                        </div>
                        <DroppableLoadStrip allocationId={allocation.id} hasItems={hasItems}>
                          {loadEqIds.map(eid => {
                            const eq = equipment.find(e => e.id === eid);
                            const label = eq ? (eq.asset_id || eq.name) : eid.slice(0, 6);
                            return (<AllocationBubble key={`eq-${eid}`} type="equipment" label={label} onRemove={() => handleRemoveLoad(allocation.id, 'equipment', eid)} />);
                          })}
                          {loadAttIds.map(aid => {
                            const att = (attachments || []).find(a => a.id === aid);
                            const label = att ? (att.name || att.attachment_type || aid.slice(0, 6)) : aid.slice(0, 6);
                            return (
                              <span key={`att-${aid}`} className="inline-flex items-center gap-0.5 text-[9px] bg-amber-600/30 text-amber-200 border border-amber-500/40 rounded px-1.5 py-0.5 whitespace-nowrap">
                                {label}
                                <button onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); handleRemoveLoad(allocation.id, 'attachment', aid); }} className="hover:text-red-300" aria-label="Remove">×</button>
                              </span>
                            );
                          })}
                          {!hasItems && (<span className="text-[9px] text-zinc-500 italic">drop assets or attachments here</span>)}
                        </DroppableLoadStrip>
                      </div>
                    );
                  })()}
                </DroppableGanttBar>
              </ResizableGanttBar>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
    <DndContext 
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-background p-4' : 'h-[calc(100vh-120px)]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(`/schedule?date=${format(currentDate, 'yyyy-MM-dd')}`)}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Schedule
            </Button>
            <h1 className="text-xl font-heading font-bold">Planning Board</h1>
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigateDate('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[180px] text-center">
              <div className="font-bold">{(() => {
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                return dayNames[currentDate.getDay()];
              })()}</div>
              <div className="text-sm text-muted-foreground">{formatDateDisplay(currentDate)}</div>
            </div>
            <Button variant="outline" size="icon" onClick={() => navigateDate('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            {isFeatureEnabled('quick_reserve') && (
              <Button variant="outline" size="sm" onClick={() => setPbReserveOpen(true)} data-testid="pb-quick-reserve-btn">
                <CalendarClock className="h-4 w-4 mr-1" />
                Quick Reserve
              </Button>
            )}
            <Button size="sm" className="btn-volt" onClick={() => handleAddJobForAsset(null)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Job
            </Button>
            <Button variant="outline" size="sm" onClick={toggleFullscreen}>
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Main Content - 3 Column Layout */}
        <div className="flex-1 grid grid-cols-[240px_1fr_240px] gap-4 min-h-0">
          
          {/* LEFT PANEL - Assets / Attachments */}
          <Card className="card-industrial flex flex-col min-h-0">
            <CardHeader className="py-3 px-4 border-b">
              {/* Tab toggle (Trailer Load P2) */}
              <div className="flex gap-1 mb-2 p-0.5 bg-muted/40 rounded" data-testid="pb-sidebar-tabs">
                <button
                  type="button"
                  onClick={() => setSidebarTab('assets')}
                  className={`flex-1 text-xs py-1 rounded transition-colors ${
                    sidebarTab === 'assets' ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="pb-sidebar-tab-assets"
                >
                  <Truck className="h-3 w-3 inline mr-1" />
                  Assets
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarTab('attachments')}
                  className={`flex-1 text-xs py-1 rounded transition-colors ${
                    sidebarTab === 'attachments' ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="pb-sidebar-tab-attachments"
                >
                  <Wrench className="h-3 w-3 inline mr-1" />
                  Attachments
                </button>
              </div>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  {sidebarTab === 'assets' ? (
                    <>
                      <Truck className="h-4 w-4" />
                      Assets ({filteredAssets.length})
                    </>
                  ) : (
                    <>
                      <Wrench className="h-4 w-4" />
                      Attachments ({filteredAttachments.length})
                    </>
                  )}
                </CardTitle>
              </div>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input
                    placeholder={sidebarTab === 'assets' ? 'Search assets...' : 'Search attachments...'}
                    value={sidebarTab === 'assets' ? assetSearch : attachmentSearch}
                    onChange={(e) => (sidebarTab === 'assets' ? setAssetSearch : setAttachmentSearch)(e.target.value)}
                    className="h-7 pl-7 text-xs"
                  />
                </div>
                {sidebarTab === 'assets' && (
                <div className="flex items-center gap-2">
                  {/* Asset Type Filter */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className={`h-7 text-xs flex-1 justify-between ${selectedAssetTypes.length > 0 ? 'border-blue-500 text-blue-500' : ''}`}
                      >
                        <div className="flex items-center gap-1">
                          <Filter className="h-3 w-3" />
                          {selectedAssetTypes.length === 0 
                            ? 'All Types' 
                            : `${selectedAssetTypes.length} Type${selectedAssetTypes.length > 1 ? 's' : ''}`
                          }
                        </div>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-2" align="start">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between pb-2 border-b mb-2">
                          <span className="text-xs font-medium">Filter by Type</span>
                          {selectedAssetTypes.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs px-2"
                              onClick={() => setSelectedAssetTypes([])}
                            >
                              Clear all
                            </Button>
                          )}
                        </div>
                        <ScrollArea className="max-h-[200px]">
                          {assetTypes.map((type) => (
                            <label 
                              key={type} 
                              className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-muted cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedAssetTypes.includes(type)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedAssetTypes(prev => [...prev, type]);
                                  } else {
                                    setSelectedAssetTypes(prev => prev.filter(t => t !== type));
                                  }
                                }}
                              />
                              <span className="text-xs truncate">{type}</span>
                              <span className="text-[10px] text-muted-foreground ml-auto">
                                ({equipment.filter(e => e.equipment_type === type).length})
                              </span>
                            </label>
                          ))}
                          {assetTypes.length === 0 && (
                            <div className="text-xs text-muted-foreground text-center py-4">
                              No asset types found
                            </div>
                          )}
                        </ScrollArea>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                )}
                {sidebarTab === 'assets' && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllocatedAssets}
                    onChange={(e) => setShowAllocatedAssets(e.target.checked)}
                    className="rounded"
                  />
                  Show allocated
                </label>
                )}
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              {sidebarTab === 'assets' ? (
              <div className="p-2 space-y-1">
                {filteredAssets.map((asset) => {
                  const allocated = isAssetAllocated(asset.id);
                  const inTransit = !allocated && isAssetInTransit(asset.id);
                  const supplierName = getSupplierName(asset.supplier_id);
                  return (
                    <DraggableResource 
                      key={asset.id} 
                      id={asset.id} 
                      type="equipment" 
                      data={asset}
                      isAllocated={allocated}
                    >
                      <div className={`p-2 rounded border transition-all hover:bg-muted/50 ${allocated ? 'border-blue-500/30 bg-blue-500/5' : inTransit ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'}`}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{asset.asset_id}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {asset.equipment_type || asset.name || 'Unknown'}
                            </div>
                            {supplierName && (
                              <div className="text-[9px] text-amber-500/80 truncate flex items-center gap-0.5">
                                <Building2 className="h-2.5 w-2.5" />
                                {supplierName}
                              </div>
                            )}
                          </div>
                          {allocated && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-400 border-blue-500/30">
                              In Use
                            </Badge>
                          )}
                          {inTransit && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 bg-amber-500/10 text-amber-400 border-amber-500/30"
                              data-testid={`pb-in-transit-${asset.id}`}
                            >
                              In Transit
                            </Badge>
                          )}
                        </div>
                      </div>
                    </DraggableResource>
                  );
                })}
                {filteredAssets.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-xs">
                    No assets found
                  </div>
                )}
              </div>
              ) : (
              <div className="p-2 space-y-1" data-testid="pb-attachments-list">
                {filteredAttachments.map((att) => {
                  const inTransit = isAttachmentInTransit(att.id);
                  const typeName = getAttachmentTypeName(att.attachment_type_id);
                  return (
                    <DraggableResource
                      key={att.id}
                      id={att.id}
                      type="attachment"
                      data={att}
                      isAllocated={inTransit}
                    >
                      <div className={`p-2 rounded border transition-all hover:bg-muted/50 ${inTransit ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'}`}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <Wrench className="h-3 w-3 text-purple-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{att.name || att.asset_id || 'Attachment'}</div>
                            {typeName && (
                              <div className="text-[10px] text-muted-foreground truncate">{typeName}</div>
                            )}
                          </div>
                          {inTransit && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1 py-0 bg-amber-500/10 text-amber-400 border-amber-500/30"
                              data-testid={`pb-att-in-transit-${att.id}`}
                            >
                              In Transit
                            </Badge>
                          )}
                        </div>
                      </div>
                    </DraggableResource>
                  );
                })}
                {filteredAttachments.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-xs">
                    No attachments found
                  </div>
                )}
              </div>
              )}
            </ScrollArea>
          </Card>

          {/* CENTER PANEL - Gantt Timeline */}
          <Card className="card-industrial flex flex-col min-h-0 overflow-hidden">
            <CardHeader className="py-3 px-4 border-b flex-shrink-0">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Jobs for {formatDateDisplay(format(currentDate, 'yyyy-MM-dd'))} ({jobsForDate.length})
              </CardTitle>
            </CardHeader>
            
            {/* Timeline Container */}
            <div className="flex-1 overflow-auto" ref={timelineRef} data-pb-timeline>
              <div style={{ minWidth: timelineWidth + 320 }}>
                {/* Time Header */}
                <div className="sticky top-0 z-20 bg-background border-b flex" style={{ height: HEADER_HEIGHT }}>
                  <div className="w-[140px] flex-shrink-0 border-r bg-muted flex items-center px-2 sticky left-0 z-30">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-auto py-1 px-1 text-[10px] w-full justify-between ${timelineAssetTypeFilter.length > 0 ? 'text-blue-400' : 'text-muted-foreground'}`}
                        >
                          <div className="flex items-center gap-1">
                            <Filter className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">
                              {timelineAssetTypeFilter.length === 0
                                ? 'All Types'
                                : `${timelineAssetTypeFilter.length} Type${timelineAssetTypeFilter.length > 1 ? 's' : ''}`
                              }
                            </span>
                          </div>
                          <ChevronDown className="h-3 w-3 flex-shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="start">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between pb-2 border-b mb-2">
                            <span className="text-xs font-medium">Filter by Type</span>
                            {timelineAssetTypeFilter.length > 0 && (
                              <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setTimelineAssetTypeFilter([])}>Clear all</Button>
                            )}
                          </div>
                          <ScrollArea className="max-h-[200px]">
                            {assetTypes.map((type) => (
                              <label key={type} className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-muted cursor-pointer">
                                <Checkbox
                                  checked={timelineAssetTypeFilter.includes(type)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setTimelineAssetTypeFilter(prev => [...prev, type]);
                                    } else {
                                      setTimelineAssetTypeFilter(prev => prev.filter(t => t !== type));
                                    }
                                  }}
                                />
                                <span className="text-xs truncate">{type}</span>
                                <span className="text-[10px] text-muted-foreground ml-auto">
                                  ({equipment.filter(e => e.equipment_type === type).length})
                                </span>
                              </label>
                            ))}
                          </ScrollArea>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="w-[180px] flex-shrink-0 border-r bg-muted flex items-center px-2 sticky left-[140px] z-30 justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Job Info</span>
                    <button
                      type="button"
                      onClick={() => setShowUnallocatedAssets(prev => !prev)}
                      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        showUnallocatedAssets
                          ? 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/50'
                          : 'text-[#ffd108] bg-[#ffd108]/15 hover:bg-[#ffd108]/25'
                      }`}
                      data-testid="pb-toggle-unallocated-assets"
                    >
                      {showUnallocatedAssets ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {showUnallocatedAssets ? 'Idle' : 'Idle'}
                    </button>
                  </div>
                  <div className="flex">
                    {TIME_SLOTS.map((slot, idx) => (
                      <div 
                        key={`col-${idx}`} 
                        className={`flex-shrink-0 border-r flex items-end pb-1 justify-center text-[10px] text-muted-foreground ${slot.minute === 0 ? 'font-medium' : 'opacity-50'}`}
                        style={{ width: SLOT_WIDTH }}
                      >
                        {slot.minute === 0 ? formatTime(slot.label, tenantSettings.time_format) : ''}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Asset Type → Asset → Jobs */}
                {jobsForDate.length === 0 && assetGroupedJobs.typeGroups.every(g => g.assets.every(a => a.jobs.length === 0)) ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No jobs scheduled for this day</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Unassigned Jobs — pinned to top */}
                    {assetGroupedJobs.unassigned.length > 0 && renderUnassignedSection()}
                    {assetGroupedJobs.typeGroups.map((typeGroup) => {
                      const isTypeCollapsed = collapsedAssets[`type_${typeGroup.type}`];
                      const totalJobs = typeGroup.assets.reduce((sum, a) => sum + a.jobs.length, 0);
                      const busyCount = typeGroup.assets.filter(a => a.jobs.length > 0).length;
                      return (
                        <div key={typeGroup.type}>
                          {/* Asset Type Header Row */}
                          <div
                            className="flex border-b border-zinc-600 cursor-pointer hover:bg-muted/30 transition-colors"
                            style={{ height: 32 }}
                            onClick={() => setCollapsedAssets(prev => ({ ...prev, [`type_${typeGroup.type}`]: !prev[`type_${typeGroup.type}`] }))}
                          >
                            <div className="w-[140px] flex-shrink-0 border-r bg-zinc-900/80 sticky left-0 z-10 flex items-center px-2 gap-1.5">
                              <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform flex-shrink-0 ${isTypeCollapsed ? '-rotate-90' : ''}`} />
                              <span className="text-[11px] font-semibold text-[#ffd108] truncate">{typeGroup.type}</span>
                            </div>
                            <div className="w-[180px] flex-shrink-0 border-r bg-zinc-900/80 sticky left-[140px] z-10 flex items-center px-2 gap-2">
                              <span className="text-[10px] text-muted-foreground">{typeGroup.assets.length} assets</span>
                              {totalJobs > 0 && <span className="text-[10px] text-zinc-500">{busyCount} in use</span>}
                            </div>
                            <div className="flex-1 bg-zinc-900/80" />
                          </div>

                          {/* Assets within this type */}
                          {!isTypeCollapsed && typeGroup.assets.map((assetEntry) => {
                            const { asset, jobs: assetJobs } = assetEntry;
                            const isAssetCollapsed = collapsedAssets[asset.id];
                            const hasJobs = assetJobs.length > 0;

                            if (!hasJobs) {
                              // Idle asset row
                              return (
                                <div key={asset.id} className="flex border-b border-zinc-800/30" style={{ height: 36 }}>
                                  <div className="w-[140px] flex-shrink-0 border-r p-1.5 pl-5 bg-muted/30 sticky left-0 z-10">
                                    <div className="text-[10px] font-medium truncate text-muted-foreground">{asset.asset_id || asset.name}</div>
                                  </div>
                                  <div className="w-[180px] flex-shrink-0 border-r bg-muted/20 sticky left-[140px] z-10 flex items-center px-2">
                                    <span className="text-[10px] text-muted-foreground/50 italic">Available</span>
                                    <button className="ml-auto text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5 transition-colors" onClick={() => handleAddJobForAsset(asset)} data-testid={`pb-add-job-idle-${asset.id}`}>
                                      <Plus className="h-3 w-3" /> Add
                                    </button>
                                  </div>
                                  <div className="flex-1 relative">
                                    <div className="absolute inset-0 flex pointer-events-none opacity-20">
                                      {TIME_SLOTS.map((slot, slotIdx) => (<div key={slotIdx} className={`flex-shrink-0 border-r ${slot.minute === 0 ? 'border-zinc-600' : 'border-zinc-800'}`} style={{ width: SLOT_WIDTH }} />))}
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            // Asset with jobs
                            const baseJobs = isAssetCollapsed ? [assetJobs[0]] : assetJobs;
                            // Setup Group nesting: inject each parent's children as contiguous rows
                            const visibleJobs = [];
                            for (const _j of baseJobs) {
                              visibleJobs.push(_j);
                              if (_j.is_setup_parent) {
                                const kids = assetGroupedJobs.groupChildrenByParentId?.get(_j.id) || [];
                                visibleJobs.push(...kids);
                              }
                            }
                            return (
                              <div key={asset.id}>
                                {visibleJobs.map((job, jobIdx) => {
                    const allocation = getJobAllocation(job.id);
                    const customer = getCustomerInfo(job.customer_id);
                    const assignedEquipment = allocation?.equipment_id ? getEquipmentInfo(allocation.equipment_id) : null;
                    const assignedOperator = allocation?.operator_id ? getOperatorInfo(allocation.operator_id) : null;
                    // Prime Mover (Phase 2.1)
                    const pbJobEqType = equipmentTypes.find(t => t.name === job.equipment_type);
                    const pbIsPrimeMoverJob = !!pbJobEqType?.requires_prime_mover;
                    const pbPrimeMoverTypeName = pbIsPrimeMoverJob
                      ? (equipmentTypes.find(t => t.id === pbJobEqType.prime_mover_asset_type_id)?.name || 'Prime Mover')
                      : null;
                    const pbAssignedPrimeMover = allocation?.prime_mover_equipment_id
                      ? getEquipmentInfo(allocation.prime_mover_equipment_id)
                      : null;
                    // Multi-trailer rigs (B-Double / Road Train) — extra trailer slots
                    const pbTrailerCount = pbIsPrimeMoverJob ? (pbJobEqType?.trailer_count || 1) : 1;
                    const pbExtraTrailerSlots = pbTrailerCount > 1 ? Array.from({ length: pbTrailerCount - 1 }).map((_, i) => {
                      const tid = (allocation?.additional_trailer_equipment_ids || [])[i];
                      return { index: i, trailerId: tid, equipment: tid ? getEquipmentInfo(tid) : null };
                    }) : [];
                    const jobWorkerAllocations = getJobWorkerAllocations(job.id);
                    const isMultiDay = job.end_date && job.end_date !== job.start_date;
                    let dayStatus = job.status;
                    // TBC overrides all status calculations
                    if (job.is_tbc) {
                      dayStatus = 'tbc';
                    } else if (isMultiDay && job.status !== 'docketed' && job.status !== 'invoiced') {
                      const hasMainAlloc = !!allocation?.equipment_id;
                      const hasOperator = !!allocation?.operator_id;
                      const hasWorkers = jobWorkerAllocations.length > 0;
                      if (!hasMainAlloc && !hasWorkers) dayStatus = 'not_allocated';
                      else if (hasMainAlloc && hasOperator) {
                        // Resolve per-day status from allocation's day_statuses
                        const currentDateStr = format(currentDate, 'yyyy-MM-dd');
                        const dayAllocStatus = (allocation?.day_statuses || {})[currentDateStr] || allocation?.status || 'draft';
                        dayStatus = dayAllocStatus === 'confirmed' ? 'confirmed' : 'allocated';
                      }
                      else dayStatus = 'partially_allocated';
                    } else if (!isMultiDay && allocation) {
                      // Single-day: derive from allocation day_statuses
                      const hasEquipment = !!allocation.equipment_id;
                      const hasOperator = !!allocation.operator_id;
                      if (hasEquipment && hasOperator) {
                        const dayAllocStatus = (allocation?.day_statuses || {})[job.start_date] || allocation?.status || 'draft';
                        if (dayAllocStatus === 'confirmed') dayStatus = 'confirmed';
                        else dayStatus = 'allocated';
                      } else {
                        dayStatus = 'partially_allocated';
                      }
                    }
                    const statusColor = getStatusColor(dayStatus);
                    const workerSlots = [];
                    (job.worker_requirements || []).forEach((wr) => {
                      const wtInfo = getWorkerTypeInfo(wr.worker_type_id);
                      for (let i = 0; i < (wr.quantity || 1); i++) {
                        const existingAllocation = jobWorkerAllocations.find(wa => wa.worker_type_id === wr.worker_type_id && jobWorkerAllocations.filter(x => x.worker_type_id === wr.worker_type_id).indexOf(wa) === i);
                        workerSlots.push({ worker_type_id: wr.worker_type_id, worker_type_name: wtInfo.name, worker_type_color: wtInfo.color, index: i, allocation: existingAllocation });
                      }
                    });
                    const effectiveJobStartTime = job._dayStartTime || job.start_time || '07:00';
                    const effectiveJobDuration = job._dayDuration || job.est_duration || 2;
                    const hasDepotTime = tenantSettings.depot_time_enabled && job.depot_time && job.depot_time !== effectiveJobStartTime;
                    const effectiveStartTime = hasDepotTime ? job.depot_time : effectiveJobStartTime;
                    const leftOffset = timeToPixel(effectiveStartTime);
                    let barWidth;
                    if (hasDepotTime && effectiveJobDuration) {
                      const startPixel = timeToPixel(effectiveJobStartTime);
                      const endPixel = startPixel + durationToPixel(effectiveJobDuration);
                      barWidth = endPixel - leftOffset;
                    } else {
                      barWidth = durationToPixel(effectiveJobDuration);
                    }
                    const startTimeMarkerOffset = hasDepotTime ? timeToPixel(effectiveJobStartTime) - leftOffset : 0;
                    const hasWorkers = workerSlots.length > 0;
                    const dynamicHeight = hasWorkers ? Math.max(ROW_HEIGHT, 80 + workerSlots.length * 22) : ROW_HEIGHT;

                    return (
                      <div key={job.id} className="flex border-b border-zinc-800/50" style={{ minHeight: dynamicHeight }}>
                        {/* Asset Column — override for Setup Group children (nested under parent) */}
                        {job.setup_group_id && !job.is_setup_parent ? (() => {
                          const childAllocation = getJobAllocation(job.id);
                          const childEquipment = childAllocation?.equipment_id ? getEquipmentInfo(childAllocation.equipment_id) : null;
                          return (
                            <div
                              className="w-[140px] flex-shrink-0 border-r p-1.5 pl-7 bg-muted/40 sticky left-0 z-10 relative"
                              data-testid={`pb-child-row-${job.id}`}
                            >
                              <span className="absolute left-2 top-1.5 text-muted-foreground/60 text-[10px]">└─</span>
                              <div className="text-[10px] font-medium truncate text-muted-foreground">{job.equipment_type}</div>
                              {childEquipment && (
                                <div className="text-[9px] text-blue-400 truncate">{childEquipment.asset_id}</div>
                              )}
                            </div>
                          );
                        })() : jobIdx === 0 ? (
                          <div
                            className="w-[140px] flex-shrink-0 border-r p-1.5 pl-5 bg-muted/80 sticky left-0 z-10 cursor-pointer hover:bg-muted transition-colors"
                            onClick={() => setCollapsedAssets(prev => ({ ...prev, [asset.id]: !prev[asset.id] }))}
                          >
                            <div className="text-[10px] font-bold truncate">{asset.asset_id || asset.name}</div>
                            {assetJobs.length > 1 && (
                              <Badge variant="outline" className="text-[8px] mt-0.5 px-1 py-0">
                                {isAssetCollapsed ? `+${assetJobs.length - 1} more` : `${assetJobs.length} jobs`}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <div className="w-[140px] flex-shrink-0 border-r bg-muted/40 sticky left-0 z-10" />
                        )}
                        {/* Job Info Column */}
                        <div className="w-[180px] flex-shrink-0 border-r p-2 bg-muted sticky left-[140px] z-10 relative group/pbcard">
                          <button data-testid={`pb-delete-job-${job.id}`} className="absolute top-1 right-1 p-0.5 rounded opacity-0 group-hover/pbcard:opacity-100 transition-opacity bg-red-500/80 hover:bg-red-600 text-white z-20" onClick={(e) => { e.stopPropagation(); setDeleteJobDialog({ open: true, job }); }} title="Delete job"><Trash2 className="h-3 w-3" /></button>
                          <div className="text-xs font-medium truncate">{customer?.company_name || 'Unknown'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{job.equipment_type}</div>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <Badge variant="outline" className={`text-[9px] ${statusColor.text} border-current`}>{dayStatus.replace('_', ' ')}</Badge>
                            {(() => { const dayInfo = getJobDayInfo(job); return dayInfo ? <Badge variant="outline" className="text-[9px] text-blue-400 border-blue-400/50 bg-blue-500/10">Day {dayInfo.dayNumber}/{dayInfo.totalDays}</Badge> : null; })()}
                          </div>
                          {workerSlots.length > 0 && (<div className="mt-1 text-[9px] text-purple-400"><UserPlus className="h-3 w-3 inline mr-1" />{workerSlots.length} workers</div>)}
                          {allocationWarnings[job.id]?.length > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="mt-1 flex items-center gap-1 cursor-help" data-testid={`pb-warning-${job.id}`}>
                                  <AlertTriangle className={`h-3 w-3 ${allocationWarnings[job.id].some(w => w.severity === 'error') ? 'text-red-400' : 'text-amber-400'}`} />
                                  <span className={`text-[9px] ${allocationWarnings[job.id].some(w => w.severity === 'error') ? 'text-red-400' : 'text-amber-400'}`}>
                                    {allocationWarnings[job.id].length} warning{allocationWarnings[job.id].length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                <div className="space-y-1">
                                  {allocationWarnings[job.id].map((w, i) => (
                                    <p key={i} className={`text-xs ${w.severity === 'error' ? 'text-red-300' : 'text-amber-300'}`}>
                                      {w.message}
                                    </p>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {/* Timeline Area */}
                        <div className="flex-1 relative">
                          <div className="absolute inset-0 flex pointer-events-none">
                            {TIME_SLOTS.map((slot, idx) => (<div key={`slot-${slot.hour}-${slot.minute}`} className={`flex-shrink-0 border-r ${slot.minute === 0 ? 'border-zinc-600' : 'border-zinc-800'}`} style={{ width: SLOT_WIDTH }} />))}
                          </div>
                          <ResizableGanttBar job={job} leftOffset={leftOffset} barWidth={Math.max(barWidth, 180)} minHeight={dynamicHeight - 16} statusColor={statusColor} isLocked={job.status === 'docketed' || job.status === 'invoiced' || job.status === 'cancelled'} onMove={handleJobMove} onResize={handleJobResize} onBarClick={handleBarClick} setupGroupTheme={null}>
                            <DroppableGanttBar jobId={job.id}>
                              {hasDepotTime && startTimeMarkerOffset > 0 && (<div className="absolute top-0 bottom-0 left-0 bg-black/15 pointer-events-none rounded-l-lg" style={{ width: startTimeMarkerOffset }} title={`Travel from depot: ${formatTime(job.depot_time, tenantSettings.time_format)} → ${formatTime(effectiveJobStartTime, tenantSettings.time_format)}`} />)}
                              {hasDepotTime && startTimeMarkerOffset > 0 && (<div className="absolute top-0 bottom-0 w-0.5 bg-[#ffd108] z-20 pointer-events-none" style={{ left: startTimeMarkerOffset }} title={`Start time: ${formatTime(effectiveJobStartTime, tenantSettings.time_format)}`}><div className="absolute -top-0.5 -left-1 w-2.5 h-1.5 bg-[#ffd108] rounded-t-sm" /></div>)}
                              <div className="flex items-center gap-1 text-[10px]">
                                {hasDepotTime && (<span className="font-mono text-[#c9a227]">Depot {formatTime(job.depot_time, tenantSettings.time_format)}</span>)}
                                <span className="font-mono font-bold">{hasDepotTime ? 'Start ' : ''}{formatTime(effectiveJobStartTime, tenantSettings.time_format)}{job.end_time && (<span className="font-normal text-muted-foreground/80 ml-0.5"> – {formatTime(job.end_time, tenantSettings.time_format)}</span>)}</span>
                                <span className="text-muted-foreground">{effectiveJobDuration ? `(${effectiveJobDuration}h)` : ''}</span>
                                <Badge variant="outline" className="text-[8px] px-1 py-0 ml-auto" data-testid={`pb-job-num-${job.id}`}>
                                  {job.job_number}
                                  {(() => {
                                    if (!job.setup_group_id || job.is_setup_parent) return null;
                                    const parent = jobs.find(j => j.is_setup_parent && j.setup_group_id === job.setup_group_id);
                                    return parent ? <span className="text-muted-foreground ml-1 font-normal">· #{parent.job_number}</span> : null;
                                  })()}
                                </Badge>
                                {job.hire_type === 'dry' && (
                                  <Badge
                                    className="text-[8px] px-1 py-0 bg-amber-500 text-white border-amber-500 hover:bg-amber-500 ml-1"
                                    data-testid={`pb-dry-pill-${job.id}`}
                                    title="Dry-hire — asset only, no operator"
                                  >
                                    DRY
                                  </Badge>
                                )}
                                {(job.hire_type === 'labour' || job.equipment_type === 'No Asset') && (
                                  <Badge
                                    className="text-[8px] px-1 py-0 bg-indigo-500 text-white border-indigo-500 hover:bg-indigo-500 ml-1"
                                    data-testid={`pb-labour-pill-${job.id}`}
                                    title="Labour hire — workers only, no asset"
                                  >
                                    LABOUR
                                  </Badge>
                                )}
                                {job.is_night_shift && <Moon className="h-3 w-3 text-indigo-400 ml-1" title="Night Shift" />}
                                {job.is_tbc && <span className="text-[8px] font-semibold text-zinc-400 bg-zinc-500/20 px-1 rounded ml-1" title="To Be Confirmed">TBC</span>}
                                {job.hire_type === 'dry' && job.hire_status && job.hire_status !== 'booked' && (
                                  <Badge
                                    className={`text-[8px] px-1 py-0 ml-1 ${
                                      job.hire_status === 'dispatched' ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-500' :
                                      job.hire_status === 'on_hire' ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-500' :
                                      job.hire_status === 'off_hired' ? 'bg-amber-600 text-white border-amber-600 hover:bg-amber-600' :
                                      job.hire_status === 'closed' ? 'bg-gray-500 text-white border-gray-500 hover:bg-gray-500' :
                                      'bg-red-500 text-white border-red-500 hover:bg-red-500'
                                    }`}
                                    data-testid={`pb-hire-status-${job.id}`}
                                    title={`Hire status: ${job.hire_status}`}
                                  >
                                    {job.hire_status.replace('_', ' ').toUpperCase()}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-wrap mt-1">
                                {assignedEquipment ? (<AllocationBubble type="equipment" label={assignedEquipment.asset_id || assignedEquipment.name} onRemove={() => handleRemoveFromAllocation(allocation.id, 'equipment_id')} />) : (<div className={`text-[9px] text-zinc-500 italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'equipment' ? 'border-blue-400 bg-blue-500/20 text-blue-400 animate-pulse' : 'border-zinc-600'}`}>+ Asset</div>)}
                                {(assignedEquipment?.combo_members || []).filter(m => m?.equipment_id).map((m, mi) => {
                                  const memEq = getEquipmentInfo(m.equipment_id);
                                  const memLabel = memEq?.asset_id || m.asset_id || memEq?.name || m.component_type_name || 'Asset';
                                  return (
                                    <span
                                      key={`pb-combo-${m.equipment_id}`}
                                      className="inline-flex items-center gap-0.5 text-[9px] bg-purple-500/25 text-purple-100 rounded px-1.5 py-0.5 border border-purple-400/40"
                                      data-testid={`pb-combo-member-${job.id}-${mi}`}
                                      title={`Combo member: ${m.component_type_name || ''} ${memLabel}`.trim()}
                                    >
                                      <Layers className="h-2.5 w-2.5" /> {memLabel}
                                    </span>
                                  );
                                })}
                                {pbExtraTrailerSlots.map((slot) => (
                                  slot.equipment
                                    ? <div key={slot.index} data-testid={`pb-trailer-assigned-${job.id}-${slot.index + 2}`}><AllocationBubble type="equipment" label={slot.equipment.asset_id || slot.equipment.name} onRemove={() => handleRemoveTrailer(allocation.id, slot.trailerId)} /></div>
                                    : <div key={slot.index} className={`text-[9px] italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'equipment' ? 'border-amber-400 bg-amber-500/20 text-amber-400 animate-pulse' : 'border-amber-500/50 text-amber-400/80'}`} data-testid={`pb-trailer-empty-${job.id}-${slot.index + 2}`}>+ Trailer {slot.index + 2}</div>
                                ))}
                                {pbIsPrimeMoverJob && (
                                  pbAssignedPrimeMover
                                    ? <div data-testid={`pb-prime-mover-assigned-${job.id}`}><AllocationBubble type="equipment" label={pbAssignedPrimeMover.asset_id || pbAssignedPrimeMover.name} onRemove={() => handleRemoveFromAllocation(allocation.id, 'prime_mover_equipment_id')} /></div>
                                    : <div className={`text-[9px] italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'equipment' ? 'border-blue-400 bg-blue-500/20 text-blue-400 animate-pulse' : 'border-blue-500/50 text-blue-400/80'}`} data-testid={`pb-prime-mover-empty-${job.id}`}>+ {pbPrimeMoverTypeName}</div>
                                )}
                                {/* Dry-hire jobs never need an operator — hide the "+ Operator" drop zone */}
                                {job.hire_type !== 'dry' && (
                                  assignedOperator ? (<AllocationBubble type="operator" label={assignedOperator.name} onRemove={() => handleRemoveFromAllocation(allocation.id, 'operator_id')} />) : (<div className={`text-[9px] text-zinc-500 italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'operator' ? 'border-green-400 bg-green-500/20 text-green-400 animate-pulse' : 'border-zinc-600'}`}>+ Operator</div>)
                                )}
                              </div>
                              {workerSlots.length > 0 && (
                                <div className="mt-1 pt-1 border-t border-zinc-600/50 flex flex-wrap gap-1">
                                  {workerSlots.map((slot, idx) => (
                                    <DroppableWorkerSlot key={`${slot.worker_type_id}-${slot.index}`} jobId={job.id} workerTypeId={slot.worker_type_id} workerTypeName={slot.worker_type_name} index={slot.index} isAssigned={!!slot.allocation} allocationId={slot.allocation?.id}>
                                      {slot.allocation ? (
                                        <AllocationBubble type="worker" label={(() => { const op = getOperatorInfo(slot.allocation.operator_id); return op ? op.name : 'Unknown'; })()} color={slot.worker_type_color} workerTypeName={slot.worker_type_name} onRemove={() => handleRemoveWorkerAllocation(slot.allocation.id)} />
                                      ) : (
                                        <div className={`text-[9px] italic border border-dashed rounded px-1.5 py-0.5 transition-all ${isDragging && activeData?.type === 'operator' ? 'border-purple-400 bg-purple-500/20 text-purple-400 animate-pulse' : 'border-zinc-600 text-zinc-500'}`} style={slot.worker_type_color ? { borderColor: `${slot.worker_type_color}40`, color: slot.worker_type_color } : {}}>+ {slot.worker_type_name}</div>
                                      )}
                                    </DroppableWorkerSlot>
                                  ))}
                                </div>
                              )}
                              {/* Trailer Load strip (P2) — visible on trailer jobs OR when items already loaded */}
                              {(() => {
                                if (!allocation) return null;
                                const loadEqIds = allocation.cargo_equipment_ids || [];
                                const loadAttIds = allocation.cargo_attachment_ids || [];
                                const hasItems = loadEqIds.length + loadAttIds.length > 0;
                                if (!hasItems && !pbIsPrimeMoverJob) return null;
                                const total = loadEqIds.length + loadAttIds.length;
                                return (
                                  <div className="mt-1 pt-1 border-t border-zinc-600/50">
                                    <div className="flex items-center gap-1 mb-0.5">
                                      <span className="text-[9px] text-amber-300/80 font-semibold tracking-wide uppercase">Load{total > 0 ? ` (${total})` : ''}</span>
                                    </div>
                                    <DroppableLoadStrip allocationId={allocation.id} hasItems={hasItems}>
                                      {loadEqIds.map(eid => {
                                        const eq = equipment.find(e => e.id === eid);
                                        const label = eq ? (eq.asset_id || eq.name) : eid.slice(0, 6);
                                        return (
                                          <AllocationBubble
                                            key={`load-eq-${eid}`}
                                            type="equipment"
                                            label={label}
                                            onRemove={() => handleRemoveLoad(allocation.id, 'equipment', eid)}
                                          />
                                        );
                                      })}
                                      {loadAttIds.map(aid => {
                                        const att = attachments.find(a => a.id === aid);
                                        const label = att ? att.name : aid.slice(0, 6);
                                        return (
                                          <span
                                            key={`load-att-${aid}`}
                                            className="inline-flex items-center gap-1 text-[9px] bg-purple-500/30 text-purple-100 rounded px-1.5 py-0.5 border border-purple-400/40"
                                            data-testid={`pb-load-att-${aid}`}
                                          >
                                            <Wrench className="h-3 w-3" /> {label}
                                            <button
                                              type="button"
                                              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); handleRemoveLoad(allocation.id, 'attachment', aid); }}
                                              className="hover:text-red-300"
                                              aria-label="Remove"
                                            >×</button>
                                          </span>
                                        );
                                      })}
                                      {!hasItems && (
                                        <span className="text-[9px] text-zinc-500 italic">drop assets or attachments here</span>
                                      )}
                                    </DroppableLoadStrip>
                                  </div>
                                );
                              })()}
                            </DroppableGanttBar>
                          </ResizableGanttBar>
                        </div>
                      </div>
                    );
                                })}
                                {/* Add Job button for this asset */}
                                {!isAssetCollapsed && (
                                  <div className="flex border-b border-zinc-800/50" style={{ height: 28 }}>
                                    <div className="w-[140px] flex-shrink-0 border-r bg-muted/40 sticky left-0 z-10" />
                                    <div className="w-[180px] flex-shrink-0 border-r bg-muted sticky left-[140px] z-10 flex items-center justify-center">
                                      <button className="text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/60 rounded px-2 py-0.5 flex items-center gap-1 transition-all border border-dashed border-transparent hover:border-muted-foreground/30" onClick={() => handleAddJobForAsset(asset)} data-testid={`pb-add-job-${asset.id}`}>
                                        <Plus className="h-3 w-3" /> Add Job
                                      </button>
                                    </div>
                                    <div className="flex-1" />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                  </>
                )}
              </div>
            </div>
          </Card>

          {/* RIGHT PANEL - Operators */}
          <Card className="card-industrial flex flex-col min-h-0">
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Workers ({filteredOperators.length})
                </CardTitle>
              </div>
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <Input
                    placeholder="Search workers..."
                    value={operatorSearch}
                    onChange={(e) => setOperatorSearch(e.target.value)}
                    className="h-7 pl-7 text-xs"
                  />
                </div>
                
                {/* Worker Type Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full h-7 justify-between text-xs"
                      data-testid="worker-type-filter-btn"
                    >
                      <span className="flex items-center gap-1.5">
                        <HardHat className="h-3 w-3" />
                        {selectedWorkerTypes.length === 0 
                          ? 'Filter by Worker Type' 
                          : `${selectedWorkerTypes.length} type${selectedWorkerTypes.length > 1 ? 's' : ''} selected`
                        }
                      </span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2" align="start">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between pb-2 border-b">
                        <span className="text-xs font-medium">Worker Types</span>
                        {selectedWorkerTypes.length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-5 px-1 text-[10px]"
                            onClick={() => setSelectedWorkerTypes([])}
                          >
                            Clear all
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {workerTypes.map((wt) => (
                          <label 
                            key={wt.id} 
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                            data-testid={`worker-type-filter-${wt.id}`}
                          >
                            <Checkbox
                              checked={selectedWorkerTypes.includes(wt.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedWorkerTypes([...selectedWorkerTypes, wt.id]);
                                } else {
                                  setSelectedWorkerTypes(selectedWorkerTypes.filter(id => id !== wt.id));
                                }
                              }}
                              className="h-3.5 w-3.5"
                            />
                            <span 
                              className="text-xs flex items-center gap-1.5"
                            >
                              <span 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: wt.color || '#8B5CF6' }}
                              />
                              {wt.name}
                            </span>
                          </label>
                        ))}
                        {workerTypes.length === 0 && (
                          <div className="text-xs text-muted-foreground text-center py-2">
                            No worker types available
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllocatedOperators}
                    onChange={(e) => setShowAllocatedOperators(e.target.checked)}
                    className="rounded"
                  />
                  Show allocated
                </label>
              </div>
            </CardHeader>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {filteredOperators.map((operator) => {
                  const allocated = isOperatorAllocated(operator.id);
                  const operatorWorkerTypes = (operator.worker_type_ids || [])
                    .map(wtId => workerTypes.find(wt => wt.id === wtId))
                    .filter(Boolean);
                  return (
                    <DraggableResource 
                      key={operator.id} 
                      id={operator.id} 
                      type="operator" 
                      data={operator}
                      isAllocated={allocated}
                    >
                      <div className={`p-2 rounded border transition-all hover:bg-muted/50 ${allocated ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`}>
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{operator.name}</div>
                            {operatorWorkerTypes.length > 0 ? (
                              <div className="flex flex-wrap gap-0.5 mt-0.5">
                                {operatorWorkerTypes.slice(0, 3).map(wt => (
                                  <span 
                                    key={wt.id}
                                    className="text-[9px] px-1 py-0 rounded"
                                    style={{ 
                                      backgroundColor: `${wt.color || '#8B5CF6'}20`,
                                      color: wt.color || '#8B5CF6'
                                    }}
                                  >
                                    {wt.name}
                                  </span>
                                ))}
                                {operatorWorkerTypes.length > 3 && (
                                  <span className="text-[9px] text-muted-foreground">
                                    +{operatorWorkerTypes.length - 3}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="text-[10px] text-muted-foreground truncate">
                                {operator.email || 'Operator'}
                              </div>
                            )}
                            {getSupplierName(operator.supplier_id) && (
                              <div className="text-[9px] text-amber-500/80 truncate flex items-center gap-0.5">
                                <Building2 className="h-2.5 w-2.5" />
                                {getSupplierName(operator.supplier_id)}
                              </div>
                            )}
                          </div>
                          {allocated && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-green-500/10 text-green-400 border-green-500/30">
                              In Use
                            </Badge>
                          )}
                          {pendingLeaveWorkerIds.has(operator.id) && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-500/10 text-amber-500 border-amber-500/30">
                              Leave requested
                            </Badge>
                          )}
                          {partDayLeaveMap[operator.id] && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-400 border-blue-500/30">
                              Part-day {partDayLeaveMap[operator.id].start_time || ''}–{partDayLeaveMap[operator.id].end_time || ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </DraggableResource>
                  );
                })}
                {filteredOperators.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-xs">
                    No workers found
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>
        </div>

        {/* Drag Overlay - Compact and semi-transparent */}
        <DragOverlay dropAnimation={null}>
          {activeId && activeData && (
            <div className="px-2 py-1 rounded-full border bg-background/80 backdrop-blur-sm shadow-lg text-xs flex items-center gap-1.5 pointer-events-none">
              {activeData.type === 'equipment' ? (
                <Truck className="h-3 w-3 text-blue-400" />
              ) : activeData.type === 'attachment' ? (
                <Wrench className="h-3 w-3 text-purple-400" />
              ) : (
                <Users className="h-3 w-3 text-green-400" />
              )}
              <span className="font-medium max-w-[80px] truncate">
                {activeData.type === 'equipment'
                  ? activeData.asset_id
                  : activeData.type === 'attachment'
                    ? (activeData.name || activeData.asset_id)
                    : activeData.name?.split(' ')[0]}
              </span>
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>

    {/* Multi-day prompt dialog */}
    <Dialog open={multiDayPrompt.open} onOpenChange={(open) => { if (!open) setMultiDayPrompt({ open: false, jobId: null, job: null, action: null, value: null }); }}>
      <DialogContent className="max-w-sm overflow-hidden" data-testid="multi-day-prompt-dialog">
        <DialogHeader>
          <DialogTitle>Multi-Day Job</DialogTitle>
          <DialogDescription className="break-words">
            Job #{multiDayPrompt.job?.job_number} runs across multiple days ({formatDateDisplay(multiDayPrompt.job?.start_date)} to {formatDateDisplay(multiDayPrompt.job?.end_date)}).
            {multiDayPrompt.action === 'move' && ' Apply this time change to:'}
            {multiDayPrompt.action === 'resize' && ' Apply this duration change to:'}
            {multiDayPrompt.action === 'allocate' && ` Allocate ${multiDayPrompt.value?.label || 'this resource'} to:`}
            {multiDayPrompt.action === 'allocate_worker' && ` Assign ${multiDayPrompt.value?.operatorName || 'operator'} as ${multiDayPrompt.value?.workerTypeName || 'worker'} for:`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 py-2 min-w-0">
          <Button
            variant="outline"
            className="justify-start h-auto py-3 whitespace-normal"
            onClick={() => handleMultiDayConfirm('day')}
            data-testid="apply-this-day-btn"
          >
            <div className="text-left min-w-0">
              <div className="font-medium">This Day Only</div>
              <div className="text-xs text-muted-foreground break-words">
                {format(currentDate, 'EEEE, d MMMM yyyy')} — other days stay unchanged
              </div>
            </div>
          </Button>
          <Button
            className="justify-start h-auto py-3 whitespace-normal"
            onClick={() => handleMultiDayConfirm('all')}
            data-testid="apply-all-days-btn"
          >
            <div className="text-left min-w-0">
              <div className="font-medium">All Days</div>
              <div className="text-xs text-muted-foreground break-words">
                {(multiDayPrompt.action === 'allocate' || multiDayPrompt.action === 'allocate_worker')
                  ? 'Allocate for every day of this job'
                  : 'Apply to every day of this job'}
              </div>
            </div>
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setMultiDayPrompt({ open: false, jobId: null, job: null, action: null, value: null })}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Job Edit Sheet - slides in from right */}
    <JobEditSheet
      jobId={editSheetJobId}
      open={editSheetOpen}
      onClose={() => { setEditSheetOpen(false); setEditSheetJobId(null); }}
      onSaved={fetchData}
      api={api}
      customers={customers}
      sites={sites}
      tenantSettings={tenantSettings}
      currentDate={currentDate}
      warnings={editSheetJobId ? (allocationWarnings[editSheetJobId] || []) : []}
    />

    <JobCreateSheet
      open={createSheetOpen}
      onClose={() => { setCreateSheetOpen(false); setCreateSheetPrefill(null); }}
      onCreated={fetchData}
      api={api}
      prefill={createSheetPrefill}
      tenantSettings={tenantSettings}
    />

    {/* Delete Job Confirmation Dialog */}
    <Dialog open={deleteJobDialog.open} onOpenChange={(open) => !open && setDeleteJobDialog({ open: false, job: null })}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="h-5 w-5" />
            {deleteJobDialog.job?.end_date && deleteJobDialog.job?.end_date !== deleteJobDialog.job?.start_date
              ? 'Remove Day from Job'
              : 'Delete Job'}
          </DialogTitle>
          <DialogDescription>
            {deleteJobDialog.job?.end_date && deleteJobDialog.job?.end_date !== deleteJobDialog.job?.start_date ? (
              <>Remove {formatDateDisplay(format(currentDate, 'yyyy-MM-dd'))} from job #{deleteJobDialog.job?.job_number}
              {deleteJobDialog.job?.customer_id && customers.find(c => c.id === deleteJobDialog.job?.customer_id) 
                ? ` — ${customers.find(c => c.id === deleteJobDialog.job?.customer_id)?.company_name}` 
                : ''}? The other days will remain.</>
            ) : (
              <>Are you sure you want to delete job #{deleteJobDialog.job?.job_number}
              {deleteJobDialog.job?.customer_id && customers.find(c => c.id === deleteJobDialog.job?.customer_id) 
                ? ` — ${customers.find(c => c.id === deleteJobDialog.job?.customer_id)?.company_name}` 
                : ''}? This action cannot be undone.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setDeleteJobDialog({ open: false, job: null })} disabled={isDeletingJob}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirmDeleteJob}
            disabled={isDeletingJob}
            data-testid="pb-confirm-delete-btn"
          >
            {isDeletingJob ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {deleteJobDialog.job?.end_date && deleteJobDialog.job?.end_date !== deleteJobDialog.job?.start_date ? 'Remove Day' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {isFeatureEnabled('quick_reserve') && (
      <QuickReserveDialog
        open={pbReserveOpen}
        onOpenChange={setPbReserveOpen}
        api={api}
        presetDate={format(currentDate, 'yyyy-MM-dd')}
        onCreated={() => toast.success('Reservation added')}
      />
    )}

    {/* Leave Override Dialog */}
    <LeaveOverrideDialog
      open={leaveOverride.open}
      blocks={leaveOverride.blocks}
      onCancel={() => {
        if (leaveOverride.onCancel) leaveOverride.onCancel();
        setLeaveOverride({ open: false, blocks: [], callback: null, onCancel: null });
      }}
      onOverride={(reason) => {
        const cb = leaveOverride.callback;
        setLeaveOverride({ open: false, blocks: [], callback: null, onCancel: null });
        if (cb) cb(reason);
      }}
    />

    </>
  );
};

export default PlanningBoard;
